import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { COLORS } from "../../theme/colors";
import { MATERIALS, RADIUS } from "../../theme/materials";
import { Badge, Button, Modal } from "../../components/ui";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { authApi, agentApi, mcpApi, webhookApi, openapiApi } from "../../api/client";
import { createLogger } from "../../utils/logger";
import DraggableNode from "./components/DraggableNode";
import CanvasConnections from "./components/CanvasConnections";
import PaletteItem from "./components/PaletteItem";
import { INITIAL_NODES, INITIAL_CONNECTIONS, PALETTE_ITEMS, PALETTE_SECTIONS, ORCHESTRATION_TEMPLATES } from "./constants";
import { inferSupervisorShape, buildSupervisorPatch, pickTemplateForLoadedSupervisor, hydrateIntegrationsAndWorkflows, hydrateSupervisorCanvasFromTree, inferIntegrationsAndWorkflows, collectUnsavedExpertPayloads, isCanvasPathological, preserveOrphanCanvasNodes, populateSupervisorIdentity, buildTreeSavePayload, collectSavedExpertIds, computeRemovedIds } from "./supervisorBridge";
import { sensitivityToConfig, configToSensitivity, sensitivityLabel, DEFAULT_ROUTING } from "../library/viewer/routingSensitivity";
import { buildRegularCanvas } from "./regularCanvasFromFlow";
import AgentChatView from "../agent-chat/AgentChatView";
// Phase B11.1: Builder canvas's AI assist now uses the same shared chat
// panel as Agent Library "Create with AI" — no behavioral drift between
// the two surfaces. Replaced the older AIAssistPanel which had its own
// minimal Cmd+K-toggled chat shell.
import AgentBuilderChat from "../agent-chat/AgentBuilderChat";
import ExpertWizard from "./ExpertWizard";
import { useAuth } from "../../contexts/AuthContext";

const log = createLogger("BuilderView");

const fmtVer = v => v ? `v${v.replace(/^v+/, "")}` : "";

// Local toolbar button helper. Standardizes the 14 buttons that line
// the canvas-mode top bar so they read as one cohesive control row
// (consistent height/padding/radius/transition). Color overrides via
// the `color` prop for action-class buttons (Save = green, Delete =
// pink, Publish = purple, etc.) — but the structural styling is
// uniform across every button.
function ToolbarBtn({
  onClick, disabled, title, color, active, iconWidth,
  children,
}) {
  const fg = disabled
    ? COLORS.textDim
    : (color || COLORS.text);
  const bg = active
    ? `${color || COLORS.accent}18`
    : "transparent";
  const w = iconWidth ?? undefined;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        height: 32,
        ...(w ? { width: w, padding: 0 } : { padding: "0 12px" }),
        borderRadius: 8, border: "none",
        background: bg, color: fg,
        fontSize: 11, fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
        transition: "background 0.12s, color 0.12s",
      }}
      onMouseEnter={(e) => {
        if (disabled || active) return;
        e.currentTarget.style.background = `${color || COLORS.surface}22`;
      }}
      onMouseLeave={(e) => {
        if (disabled || active) return;
        e.currentTarget.style.background = bg;
      }}
    >
      {children}
    </button>
  );
}

const BuilderView = ({ pendingEditAgent, clearPendingEdit, deepLinkAgentId }) => {
  const bp = useBreakpoint();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  // 2026-05-24 deep-link fix: URL /builder/<uuid> now loads the agent
  // directly. Previously pendingEditAgent was the only entry point (set
  // by AgentLibraryView's onEditAgent → Shell → BuilderView), so pasting
  // a Builder URL into a fresh tab showed a blank canvas. The effect
  // below fires when (a) the URL carries an :agentId (passed by Shell
  // as the deepLinkAgentId prop — useParams doesn't work here because
  // App's outer route is path="/*" splat with no named params), AND
  // (b) no pendingEditAgent prop was passed (i.e. user didn't arrive
  // via the Library click path). The prop path keeps priority.
  const [deepLinkLoading, setDeepLinkLoading] = useState(false);
  const [deepLinkError, setDeepLinkError] = useState(null);
  const [deepLinkAgent, setDeepLinkAgent] = useState(null);
  const [builderMode, setBuilderMode] = useState("canvas"); // "canvas" | "guided"
  const [nodes, setNodes] = useState(INITIAL_NODES);
  const [connections, setConnections] = useState(INITIAL_CONNECTIONS);
  const [selectedNode, setSelectedNode] = useState("llm-1");
  const [draggedItem, setDraggedItem] = useState(null);
  const [temperature, setTemperature] = useState(0.7);
  const [systemPrompt, setSystemPrompt] = useState("You are a friendly and professional customer support agent for our company. Always greet the customer warmly, answer questions using only the provided knowledge base, never fabricate information, and escalate to a human agent if you cannot resolve the issue within 3 turns.");
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [promptFocused, setPromptFocused] = useState(false);
  const [connectingFrom, setConnectingFrom] = useState(null);
  const [mousePos, setMousePos] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [reconnectDrag, setReconnectDrag] = useState(null); // { connIndex, fromNode }
  const [canvasDragOver, setCanvasDragOver] = useState(false);
  const [zoom, setZoom] = useState(85);
  const [nodeCounter, setNodeCounter] = useState(2);
  const [undoStack, setUndoStack] = useState([]);
  // Components palette visibility — true by default on desktop (was
  // gated to mobile via the `bp.isDesktop || showPalette` clause; now
  // every viewport respects this state so the user can hide it).
  // Persisted across sessions in localStorage.
  const [showPalette, setShowPalette] = useState(() => {
    try {
      const saved = localStorage.getItem("builder.palette.open");
      return saved === null ? true : saved === "1";
    } catch { return true; }
  });
  // Phase B11.1: AI assist chat (shared AgentBuilderChat). Cmd+K toggles
  // open/close; persists across sessions in localStorage to match the
  // behavior the older AIAssistPanel had.
  const [aiAssistOpen, setAiAssistOpen] = useState(() => {
    try { return localStorage.getItem("builder.ai_assist.open") === "1"; } catch { return false; }
  });
  // BUG-023 — auto-collapse AI Assist when a node is selected so the
  // Node Config panel (right:0, z-150, width 280) doesn't render
  // behind the AI Assist panel (right:0, z-1000, width 420).
  //
  // State model:
  //   aiAssistAutoCollapseRef.mounted — guards against firing on
  //     INITIAL mount. selectedNode defaults to "llm-1" so without
  //     this guard the auto-collapse would fire on every page load
  //     (the selectedNode useEffect runs on mount with the initial
  //     value). We only want to react to USER actions (node clicks),
  //     not the React lifecycle.
  //   aiAssistAutoCollapseRef.persistSkip — set just before an
  //     auto-state-change to make the localStorage-write useEffect
  //     skip persisting that transition. Consumed on next fire.
  //   aiAssistAutoCollapseRef.pendingRestore — set when the auto-
  //     collapse fires; consumed when the user deselects the node
  //     (we then auto-restore AI Assist). User-initiated toggles
  //     (Cmd+K, toolbar button) clear pendingRestore so a manual
  //     intent isn't overridden by an automatic restore.
  const aiAssistAutoCollapseRef = useRef({
    mounted: false,
    persistSkip: false,
    pendingRestore: false,
  });
  const [showConfig, setShowConfig] = useState(false);
  const [llmConfig, setLlmConfig] = useState(null);
  const [llmApiKey, setLlmApiKey] = useState("");
  const [llmProvider, setLlmProvider] = useState("");  // BYOK-strict: filled from tenant config
  const [llmModel, setLlmModel] = useState("");
  const [llmMaxTokens, setLlmMaxTokens] = useState(2048);
  const [llmTesting, setLlmTesting] = useState(false);
  const [llmTestResult, setLlmTestResult] = useState(null);
  const [llmRankingsMode, setLlmRankingsMode] = useState(false);
  const [llmRankings, setLlmRankings] = useState([]);
  // BYOK-strict: no hardcoded model list. `availableModels` is populated
  // at runtime from GET /api/llm/models, which returns only models the
  // tenant has API keys for. Empty object = tenant has no provider
  // configured; UI renders <LLMNotConfigured/> in that case.
  const [availableModels, setAvailableModels] = useState({});
  const [llmConfigured, setLlmConfigured] = useState(null);
  const [triggerChannels, setTriggerChannels] = useState({ whatsapp: true, voice: true, chat: true, sms: false, email: false, webhook: false });
  const [voiceConfig, setVoiceConfig] = useState({ voice_id: "", model: "eleven_turbo_v2_5", stability: "0.35", similarity_boost: "0.8", style: "0.4", speed: "1.05", outbound_disclosure_enabled: true });
  const [routingIntents, setRoutingIntents] = useState([]);
  // C3: margin-routing sensitivity authored from the supervisor node. null =
  // "not touched here" → the save merge preserves whatever the Viewer / auto-tune
  // persisted; an object once the user moves the slider → written to
  // flow_config.supervisor.routing via buildSupervisorPatch.
  const [routingConfig, setRoutingConfig] = useState(null);
  const [isDefault, setIsDefault] = useState(false);
  const [isDynamicAgent, setIsDynamicAgent] = useState(false);
  const [agentPriority, setAgentPriority] = useState(100);
  const [newIntent, setNewIntent] = useState("");
  const [triggerName, setTriggerName] = useState("");
  const [availableWebhooks, setAvailableWebhooks] = useState([]);
  const [selectedWebhookId, setSelectedWebhookId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [savedAgents, setSavedAgents] = useState([]);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [currentAgentId, setCurrentAgentId] = useState(null);
  // D3 (H3): the tree_version last seen for the loaded agent. Sent as
  // expected_tree_version on save so a concurrent edit from another tab trips
  // the backend's 412 stale_tree_version guard instead of silently clobbering.
  // A ref (not state) — it never drives render, only the next save's token.
  const treeVersionRef = useRef(0);
  // D9: the expert agent_ids and workflow ids present when the agent's tree was
  // loaded. At save we diff these against the canvas to emit removed_*_ids so a
  // deleted expert/workflow is actually soft-deleted (apply_tree needs the
  // explicit list — it doesn't reconcile experts declaratively).
  const loadedExpertIdsRef = useRef(new Set());
  const loadedWorkflowIdsRef = useRef(new Set());
  const captureTreeBaseline = useCallback((treeRes) => {
    const experts = treeRes?.data?.experts || [];
    const workflows = treeRes?.data?.workflows || [];
    loadedExpertIdsRef.current = new Set(
      experts.map((e) => e?.agent_id || e?.id).filter(Boolean)
    );
    loadedWorkflowIdsRef.current = new Set(
      workflows.map((w) => w?.id).filter(Boolean)
    );
  }, []);
  const [agentName, setAgentName] = useState("Untitled Agent");
  const [agentVersion, setAgentVersion] = useState("v1.0");
  const [showSaveAsModal, setShowSaveAsModal] = useState(false);
  const [saveAsName, setSaveAsName] = useState("");
  const [saveAsVersion, setSaveAsVersion] = useState("v1.0");
  const [showSaveNewModal, setShowSaveNewModal] = useState(false);
  const [saveNewName, setSaveNewName] = useState("");
  const [saveNewVersion, setSaveNewVersion] = useState("v1.0");
  const [availableAgents, setAvailableAgents] = useState([]);
  const [mcpServers, setMcpServers] = useState([]);
  // C4 — dynamic palette items for the Integrations section. (WS4 retired the
  // Workflows section + its `tenantWorkflows` state.)
  const [openapiIntegrations, setOpenapiIntegrations] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState({});
  // C10 — flow_config.canvas.node_labels keyed by node.id. Save flow
  // serializes this into the agent's flow_config so the Viewer's GraphTab
  // can render the same custom labels as the Builder.
  const [customNodeLabels, setCustomNodeLabels] = useState({});
  // C16-C18 — inline expert authoring wizard. Opened on Sub-Agent drop
  // when the new node has no source agent; closed by Save (commits via
  // /tree atomic save) or Cancel.
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardForNodeId, setWizardForNodeId] = useState(null);
  // C23 — Export / Import / Publish modals.
  const [showImportModal, setShowImportModal] = useState(false);
  const [importJsonText, setImportJsonText] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [importErr, setImportErr] = useState(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishName, setPublishName] = useState("");
  const [publishDescription, setPublishDescription] = useState("");
  const [publishCategory, setPublishCategory] = useState("general");
  const [publishVisibility, setPublishVisibility] = useState("tenant");
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishErr, setPublishErr] = useState(null);
  const [nodeConfigs, setNodeConfigs] = useState({
    knowledge: { enabled: true, top_k: 8, score_threshold: 0.30 },
    input_guardrails: { enabled: true, pii_detection: true, injection_detection: true, blocked_topics: [] },
    output_guardrails: { enabled: true, prohibited_phrases: [] },
    fact_checker: { enabled: false, level: "standard", confidence_threshold: 0.4, verify_numbers: true, require_sources: false },
    condition: {
      enabled: true,
      rules: [
        { intent: "complaint", action: "escalate", label: "Escalate complaints" },
        { intent: "billing", action: "continue", label: "Handle billing" },
      ],
      default_action: "continue",
      confidence_threshold: 0.6,
    },
    action: {
      send_response: { enabled: true, format: "text" },
      escalation: { enabled: true, intent_trigger: true, history_threshold: 12, phrases: [], message: "Let me connect you with a human agent." },
      handoff: {
        whatsapp: { mode: "external_webhook", webhook_url: "", auth_header: "", agent_number: "", agent_name: "", confirmation_message: "You've been connected to a human agent. Please wait for a response." },
        chat: { queue_name: "" },
        voice: { transfer_number: "", ivr_queue_id: "" },
        sms: { mode: "external_webhook", webhook_url: "", auth_header: "", forward_number: "", confirmation_message: "You've been connected to a human agent. Please wait for a response." },
      },
    },
    api: { enabled: true, max_tool_calls: 5, timeout: 30, error_handling: "continue", allowed_servers: [] },
    memory: { enabled: true, history_window: 20, search_type: "recent", index_pattern: "conversations-*" },
    preprocess: {
      language_detection: { enabled: true },
      intent_classification: { enabled: true },
      sentiment_analysis: { enabled: true },
      attachment_processing: { enabled: true },
    },
    function: {},
    grounding: { enabled: true, data_store_ids: [] },
    internal_tools: { enabled: false, toolsets: [] },
    llm_smart_routing: { enabled: false, use_llm_classifier: false, tiers: {} },
  });
  const updateNodeConfig = (nodeType, path, value) => {
    setNodeConfigs(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let target = updated[nodeType];
      for (let i = 0; i < keys.length - 1; i++) target = target[keys[i]];
      target[keys[keys.length - 1]] = value;
      return updated;
    });
  };
  const updateAgentNodeConfig = (nodeId, updates) => {
    setNodes(prev => prev.map(n => {
      if (n.id !== nodeId) return n;
      const newConfig = { ...n.agentConfig, ...updates };
      const label = updates.agentName || n.label;
      return { ...n, agentConfig: newConfig, label };
    }));
  };
  const canvasRef = useRef(null);
  // Pan state — middle-mouse-drag OR shift+left-click-drag pans the
  // canvas viewport for navigating large supervisor agents (BUG-024).
  // Native scrollbars stay on top as a fallback; this is the "click and
  // drag the cluster" affordance.
  const panState = useRef({ panning: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 });

  // Fit-to-view — compute the zoom + scroll position that shows every
  // node in the viewport. Called from a toolbar button and auto-fired
  // when a fresh canvas hydrates (BUG-024 follow-up: 22-node OC-01
  // didn't fit at default 85% and the user had no obvious way to zoom
  // out far enough). The fitPendingRef tracks whether a hydration just
  // landed so the next render commit can fit (state-update timing —
  // can't synchronously compute extents inside setNodes).
  const fitPendingRef = useRef(false);
  const fitToView = useCallback((opts) => {
    const el = canvasRef.current;
    if (!el || !nodes || nodes.length === 0) return;
    const padding = (opts && opts.padding) || 80;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of nodes) {
      const nx = n.x || 0, ny = n.y || 0;
      // Estimated node size — actual size varies (140-220 wide, 50-70 tall)
      // but using a typical 200x60 envelope makes the fit slightly generous,
      // which is what we want (some padding around the cluster).
      minX = Math.min(minX, nx);
      minY = Math.min(minY, ny);
      maxX = Math.max(maxX, nx + 200);
      maxY = Math.max(maxY, ny + 60);
    }
    if (!isFinite(minX)) return;
    const contentW = maxX - minX + padding * 2;
    const contentH = maxY - minY + padding * 2;
    const viewW = el.clientWidth;
    const viewH = el.clientHeight;
    const scaleX = viewW / contentW;
    const scaleY = viewH / contentH;
    // Floor of 0.5 (50%) — anything tighter and the node labels are
    // illegible. When the cluster is too wide for 50%, scrollbars +
    // click-drag pan let the user navigate the parts not in view.
    // Without this floor, a 21-node OC-01 auto-fits to ~26% and
    // labels become unreadable (user-reported BUG-024 follow-up).
    const scale = Math.max(0.5, Math.min(1, Math.min(scaleX, scaleY)));
    const newZoom = Math.round(scale * 100);
    setZoom(newZoom);
    // After the next render commit, scroll so the cluster's top-left
    // is at the viewport's top-left + padding. When the cluster
    // exceeds the viewport at the clamped zoom, this leaves the
    // RIGHT portion off-screen — the user pans or scrolls right.
    requestAnimationFrame(() => {
      if (!canvasRef.current) return;
      canvasRef.current.scrollLeft = Math.max(0, (minX - padding) * scale);
      canvasRef.current.scrollTop  = Math.max(0, (minY - padding) * scale);
    });
  }, [nodes]);

  // Auto-fit whenever a hydration just landed (load handlers set
  // fitPendingRef.current = true). Triggers on the *next* nodes-state
  // change after the flag is set so the new positions are committed.
  useEffect(() => {
    if (fitPendingRef.current && nodes && nodes.length > 0) {
      fitPendingRef.current = false;
      // One paint frame later, otherwise canvasRef.current.client* is stale.
      requestAnimationFrame(() => fitToView());
    }
  }, [nodes, fitToView]);

  // Toolbar (bottom action bar) is floating + draggable per user
  // feedback 2026-05-22. Default is null/null = centered at bottom;
  // dragging the handle sets concrete pixel coords (relative to the
  // builder root container). Persisted in localStorage.
  const [toolbarPos, setToolbarPos] = useState(() => {
    try {
      const raw = localStorage.getItem("builder.toolbar.pos");
      return raw ? JSON.parse(raw) : { x: null, y: null };
    } catch { return { x: null, y: null }; }
  });
  const toolbarDragState = useRef(null);
  const handleToolbarDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Capture the toolbar's current viewport position as the drag base
    // — works whether the toolbar is at its default centered spot or
    // already custom-positioned. (Toolbar is position:fixed so
    // getBoundingClientRect is viewport-relative.)
    const toolbarEl = e.currentTarget.parentElement;
    const rect = toolbarEl ? toolbarEl.getBoundingClientRect() : { left: 0, top: 0 };
    toolbarDragState.current = {
      startX: e.clientX, startY: e.clientY,
      baseX: rect.left, baseY: rect.top,
    };
    const onMove = (ev) => {
      const s = toolbarDragState.current;
      if (!s) return;
      const dx = ev.clientX - s.startX;
      const dy = ev.clientY - s.startY;
      const nextX = Math.max(0, Math.min(window.innerWidth - 80, s.baseX + dx));
      const nextY = Math.max(0, Math.min(window.innerHeight - 40, s.baseY + dy));
      setToolbarPos({ x: nextX, y: nextY });
    };
    const onUp = () => {
      toolbarDragState.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  // Persist whenever the position state settles.
  useEffect(() => {
    try {
      localStorage.setItem("builder.toolbar.pos", JSON.stringify(toolbarPos));
    } catch {}
  }, [toolbarPos]);

  useEffect(() => {
    authApi.getLlmConfig().then(res => {
      if (res.success) {
        setLlmConfig(res.data);
        // Tenant-wide model/max_tokens/temperature no longer exist — the
        // /llm-config response is API-key-only. useState defaults (line 24
        // area: temperature=0.7, llmMaxTokens=2048, llmModel="") apply for
        // new agents. For existing agents, the load-agent branches below
        // populate these from agent.flow_config.llm explicitly.
      }
    }).catch(() => {});
    // BYOK-strict: hit /api/llm/models (tenant-scoped). 412 means no provider
    // configured → show empty state; anything else → keep as-is and surface error.
    import("../../api/client").then(({ llmApi }) => {
      llmApi.models().then(res => {
        const data = res?.data || {};
        setAvailableModels(data);
        setLlmConfigured(true);
        // New-agent flow: llmProvider starts at "" (no agent to load it
        // from), but the Provider <select> has no empty placeholder
        // option — the browser falls back to visually showing its first
        // option ("OpenAI") while React's controlled value stays "".
        // That mismatch left the Model dropdown computing
        // availableModels[""] (always empty) until the user manually
        // re-picked a provider. Sync llmProvider/llmModel to the first
        // actually-configured provider once models load, but only when
        // nothing's set yet — an existing agent's loaded provider (set
        // by the agent-load effects) must never be clobbered, whichever
        // effect happens to resolve first.
        //
        // Must match the Model <select>'s own `kind === "chat"` filter
        // (line ~2471) — a provider's raw list can lead with a non-chat
        // entry (e.g. OpenAI's list starts with the embedding model
        // text-embedding-ada-002), which isn't a rendered <option> at
        // all. Picking that as the default reproduces the exact same
        // select/state mismatch this fix is meant to close.
        const firstProv = Object.keys(data).find(
          p => (data[p] || []).some(m => m.kind === "chat")
        ) || "";
        if (firstProv) {
          const firstModel = (data[firstProv] || []).find(m => m.kind === "chat")?.id || "";
          setLlmProvider(prev => prev || firstProv);
          setLlmModel(prev => prev || firstModel);
        }
      }).catch(err => {
        if (err?.status === 412) {
          setAvailableModels({});
          setLlmConfigured(false);
        }
      });
    });
  }, []);

  // Phase B11.1: Cmd+K (or Ctrl+K) toggles the AI assist chat panel.
  // Persist open state across sessions so a user who opened it once
  // gets it back next time they enter the canvas.
  // BUG-023: skip the persist if this state change came from an
  // auto-collapse (node-select) — otherwise the user's preference
  // gets clobbered to "closed" and they lose AI Assist on next
  // session. persistSkip is consumed on each handled change.
  useEffect(() => {
    if (aiAssistAutoCollapseRef.current.persistSkip) {
      aiAssistAutoCollapseRef.current.persistSkip = false;
      return;
    }
    // User-initiated change (Cmd+K or toolbar) → clear pendingRestore
    // so a later node-deselect doesn't override the user's intent.
    aiAssistAutoCollapseRef.current.pendingRestore = false;
    try { localStorage.setItem("builder.ai_assist.open", aiAssistOpen ? "1" : "0"); } catch {}
  }, [aiAssistOpen]);

  // BUG-023 — auto-collapse AI Assist on node-select; auto-restore
  // on deselect when the collapse fired in this session. State-only
  // change; no z-index / position / dock-mode touched.
  //
  // The mounted-ref skip on first fire is critical: selectedNode
  // defaults to "llm-1" so without this guard the auto-collapse
  // would fire on every page mount, closing AI Assist whenever the
  // user reopens the Builder. We only want to react to USER actions
  // (node clicks), not React's initial-mount useEffect cycle.
  useEffect(() => {
    const ref = aiAssistAutoCollapseRef.current;
    if (!ref.mounted) {
      ref.mounted = true;
      return;
    }
    if (selectedNode && aiAssistOpen) {
      ref.persistSkip = true;
      ref.pendingRestore = true;
      setAiAssistOpen(false);
    } else if (!selectedNode && ref.pendingRestore) {
      ref.persistSkip = true;
      ref.pendingRestore = false;
      setAiAssistOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode]);
  useEffect(() => {
    const onKey = (e) => {
      const isToggle = (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      if (!isToggle) return;
      e.preventDefault();
      setAiAssistOpen(prev => !prev);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    agentApi.list().then(res => {
      if (res.success) setAvailableAgents(res.data || []);
    }).catch(() => {});
    mcpApi.listServers().then(res => {
      if (res.success) setMcpServers(res.data?.servers || []);
    }).catch(() => {});
    openapiApi.list().then(res => {
      if (res?.success) setOpenapiIntegrations(res.data?.integrations || res.data || []);
    }).catch(() => setOpenapiIntegrations([]));
    // WS4: the tenant-workflows fetch fed only the retired Workflows palette
    // section — removed with it (workflowApi stays available for the deprecated
    // bind/unbind + tree-save plumbing that WS4 deliberately keeps).
    webhookApi.list().then(res => {
      if (res.success) setAvailableWebhooks(res.data || []);
    }).catch(() => {});
  }, []);

  // C9 — Fetch live bindings (B4 /wiring) + workflows (B1 /tree) and
  // append corresponding canvas nodes after the initial hydration.
  // Reused by both load paths (pendingEditAgent useEffect + handleLoadAgent).
  //
  // savedCanvasNodes (optional): the agent's previously-saved
  // metadata_json.canvas.nodes. When provided, this function also
  // preserves any integration/workflow nodes in the saved canvas
  // whose binding is no longer in /wiring (soft-deleted bindings),
  // so the user doesn't lose nodes when a binding gets cascaded away.
  const _appendBindingsForAgent = useCallback(async (agentId, savedCanvasNodes) => {
    if (!agentId) return;
    try {
      const [wiringRes, treeRes] = await Promise.all([
        agentApi.getWiring(agentId).catch(() => null),
        agentApi.getTree(agentId).catch(() => null),
      ]);
      const wiring = wiringRes?.data || null;
      const treeWorkflows = treeRes?.data?.workflows || [];
      // Compute orphan saved-canvas nodes: integration/workflow nodes
      // the user previously placed whose binding no longer appears in
      // /wiring or /tree (real symptom on OC-01: supabase MCP binding
      // soft-deleted, node would silently vanish on re-hydrate). Key
      // by `${kind}:${bound_id}` so duplicate nodes wired to different
      // agents but the same integration are treated as a single key.
      const currentBoundKeys = new Set();
      for (const bnd of (wiring?.bindings || [])) {
        if (bnd?.integration_kind && bnd?.integration_id) {
          currentBoundKeys.add(`${bnd.integration_kind}:${bnd.integration_id}`);
        }
      }
      for (const wf of treeWorkflows) {
        if (wf?.id) currentBoundKeys.add(`workflow:${wf.id}`);
      }
      const orphans = [];
      const seenOrphanKeys = new Set();
      for (const sn of (savedCanvasNodes || [])) {
        if (!sn) continue;
        const isBindable = sn.type === "mcp_integration"
                         || sn.type === "openapi_integration"
                         || sn.type === "workflow";
        if (!isBindable || !sn.bound_id) continue;
        const kind = sn.bound_kind || (
          sn.type === "mcp_integration" ? "mcp"
          : sn.type === "openapi_integration" ? "openapi"
          : "workflow"
        );
        const key = `${kind}:${sn.bound_id}`;
        if (currentBoundKeys.has(key) || seenOrphanKeys.has(key)) continue;
        seenOrphanKeys.add(key);
        // Stash the saved x/y for later — actual placement is computed
        // in the setNodes block below where we know the first-expert
        // anchor and can position the orphan consistently below it
        // (matching the live-integration layout).
        // A4 (agent-surface-consistency): tag orphan tiles so the serializer
        // (inferIntegrationsAndWorkflows) doesn't re-emit them on save and
        // reactivate the soft-deleted binding the user removed.
        orphans.push({ ...sn, _orphan: true });
      }
      if (!wiring && treeWorkflows.length === 0 && orphans.length === 0) return;
      // Late async hydration: integration + workflow nodes land here
      // AFTER the experts hydrate. Re-arm fit so it accounts for the
      // wider cluster (bindings push the rightmost X out 200px+ each).
      fitPendingRef.current = true;
      // Stash the anchor id we want each orphan to attach to once the
      // canvas state has the hydrated experts. First-expert is the
      // sensible fallback: the orphan was originally wired to either
      // the supervisor (no canvas node) or a specific expert that may
      // have moved. Re-pinning to expert-1 keeps it visually attached
      // until the user re-wires deliberately.
      let orphanAnchorId = null;
      let orphanAnchorX = 80, orphanAnchorY = 260;
      // 2026-05-24 fix: capture the just-computed nodes from the
      // setNodes updater (which sees the latest committed nodes via
      // prevNodes) and reuse them in the setConnections updater
      // below. Pre-fix, setConnections used the closure-captured
      // `nodes` useState from useCallback's empty deps — that's the
      // INITIAL nodes value (default canvas template), never updated.
      // Anchor lookups against that stale state produced edges to
      // template IDs (agent-2 / agent-3 from default) that don't
      // exist in the actually-rendered canvas, dangling on a
      // supervisor-anchored integration binding.
      let computedNodes = null;
      setNodes(prevNodes => {
        const result = hydrateIntegrationsAndWorkflows({
          nodes: prevNodes,
          connections: [],  // we'll merge connections via setConnections separately
          wiring,
          treeWorkflows,
        });
        // Pick the anchor for orphans: prefer the first expert, fall
        // back to the trigger so a no-expert supervisor still wires
        // its orphans to something rather than leaving them floating.
        const firstAgent = result.nodes.find(n =>
          n && (n.type === "agent" || n.type === "llm")
        );
        const trigger = result.nodes.find(n => n && n.type === "trigger");
        const anchor = firstAgent || trigger;
        orphanAnchorId = anchor?.id || null;
        orphanAnchorX = anchor?.x ?? 80;
        orphanAnchorY = anchor?.y ?? 260;
        // Count how many LIVE bindings are already below the first
        // expert so orphan(s) stack underneath them, not on top.
        const liveBelowFirstExpert = result.nodes.filter(n =>
          (n?.type === "mcp_integration" || n?.type === "openapi_integration")
          && Math.abs((n.x || 0) - orphanAnchorX) < 5
        ).length;
        const placedOrphans = orphans.map((o, idx) => ({
          ...o,
          // Drop the orphan immediately below the first expert in the
          // same vertical column as live integrations. Stack additional
          // orphans 70px further down.
          x: orphanAnchorX,
          y: orphanAnchorY + 100 + (liveBelowFirstExpert + idx) * 70,
        }));
        computedNodes = [...result.nodes, ...placedOrphans];
        return computedNodes;
      });
      setConnections(prevConns => {
        // Use the just-computed nodes (captured from setNodes
        // updater) for the anchor-lookup pass. React processes
        // setState updaters in call order, so computedNodes is
        // populated by the time this runs. Fallback to prevConns
        // if for any reason setNodes hasn't fired (shouldn't
        // happen in practice).
        if (!computedNodes) return prevConns;
        const result = hydrateIntegrationsAndWorkflows({
          nodes: computedNodes,
          connections: prevConns,
          wiring,
          treeWorkflows,
        });
        // Add a provides_tools / triggered_by edge for each orphan
        // so it doesn't float disconnected on the canvas. Without this
        // the user sees a node with no incoming/outgoing wiring and
        // wonders what it is (real complaint from staging OC-01 review).
        const orphanEdges = [];
        if (orphanAnchorId) {
          for (const o of orphans) {
            if (o.type === "workflow") {
              orphanEdges.push({ from: orphanAnchorId, to: o.id, type: "triggered_by" });
            } else {
              orphanEdges.push({ from: o.id, to: orphanAnchorId, type: "provides_tools" });
            }
          }
        }
        return [...result.connections, ...orphanEdges];
      });
    } catch (e) {
      log.warn("hydrate bindings/workflows failed", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link fetch: when URL carries /builder/:agentId AND the
  // user didn't arrive via the prop path (pendingEditAgent null),
  // fetch the agent and feed it into the same load pipeline as
  // pendingEditAgent below. Clears prior deep-link state when the
  // URL id changes (browser back/forward, pasting a new URL).
  useEffect(() => {
    if (!deepLinkAgentId || pendingEditAgent) {
      setDeepLinkAgent(null);
      setDeepLinkError(null);
      return;
    }
    let cancelled = false;
    setDeepLinkLoading(true);
    setDeepLinkError(null);
    setDeepLinkAgent(null);
    agentApi.get(deepLinkAgentId)
      .then((res) => {
        if (cancelled) return;
        const agentRow = res?.data || res;
        if (!agentRow || !agentRow.id) {
          setDeepLinkError("Agent not found");
        } else {
          setDeepLinkAgent(agentRow);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.message || String(err);
        setDeepLinkError(
          /404/i.test(msg) || /not found/i.test(msg)
            ? "Agent not found (the URL may be stale or you may not have access)"
            : `Failed to load agent: ${msg}`
        );
      })
      .finally(() => {
        if (!cancelled) setDeepLinkLoading(false);
      });
    return () => { cancelled = true; };
  }, [deepLinkAgentId, pendingEditAgent]);

  useEffect(() => {
    const incomingAgent = pendingEditAgent || deepLinkAgent;
    if (incomingAgent) {
      const agent = incomingAgent;
      const canvas = agent.metadata_json?.canvas;
      // BUG-024 self-heal: for supervisor agents we ALWAYS re-hydrate
      // positions from /tree, not from the saved canvas. The tree is
      // the source of truth — experts added to the family after the
      // canvas was last saved (real symptom on staging OC-01: 9 experts
      // in /tree, only 6 specialist nodes in the saved canvas) won't
      // appear if we trust saved positions. The earlier pathological-
      // heuristic was too narrow (only fired when nodes were piled in
      // one X-bucket). canvas.nodeConfigs / canvas.config still merge
      // below so tuning sliders, prompts, channels survive — only
      // POSITIONS are re-derived.
      const stalePositions = (
        agent.agent_kind === "supervisor" && canvas?.nodes
      );
      // Auto-fit-to-view on every load — a 22-node OC-01 doesn't fit at
      // default 85% zoom and the user shouldn't need to figure out
      // ctrl+wheel to see the whole graph (BUG-024 follow-up).
      fitPendingRef.current = true;
      if (canvas) {
        if (stalePositions) {
          const supervisorTemplate = pickTemplateForLoadedSupervisor(agent);
          if (supervisorTemplate) {
            // Populate supervisor identity even in the stock template
            // render so _appendBindingsForAgent's anchor lookups
            // succeed under timing race against /tree (Bug 2A).
            setNodes(populateSupervisorIdentity(supervisorTemplate.nodes, agent));
            setConnections(supervisorTemplate.connections.map(c => ({ ...c })));
            setNodeCounter(supervisorTemplate.nodes.length + 1);
            agentApi.getTree(agent.id).then(treeRes => {
              captureTreeBaseline(treeRes);  // D9: record loaded expert/workflow ids
              const built = hydrateSupervisorCanvasFromTree({
                template: supervisorTemplate,
                experts: treeRes?.data?.experts || [],
                mode: agent.flow_config?.supervisor?.mode,
                agent,
              });
              if (!built) return;
              fitPendingRef.current = true;
              setNodes(built.nodes);
              setConnections(built.connections);
              setNodeCounter(built.nodes.length + 1);
              // Orphan-canvas preservation runs LATER inside
              // _appendBindingsForAgent, which has access to /wiring +
              // /tree workflows at the same time so it can distinguish
              // "still bound" from "orphan" saved nodes correctly.
            }).catch(() => { /* keep stock template on /tree failure */ });
          } else if (canvas.nodes) {
            // Not a supervisor (false positive) — fall back to saved.
            setNodes(canvas.nodes);
            setConnections(canvas.connections);
          }
          setZoom(100);
        } else {
          if (canvas.nodes) setNodes(canvas.nodes);
          if (canvas.connections) setConnections(canvas.connections);
          if (canvas.zoom) setZoom(canvas.zoom);
          if (canvas.nodeCounter) setNodeCounter(canvas.nodeCounter);
        }
        // Configs (prompt, channels, tuning) are merged in BOTH branches —
        // re-laying out positions shouldn't lose the user's tuning.
        if (canvas.nodeConfigs) {
          setNodeConfigs(prev => {
            const nc = { ...prev, ...canvas.nodeConfigs };
            // Migrate legacy: function → preprocess
            if (!canvas.nodeConfigs.preprocess && canvas.nodeConfigs.function &&
                (canvas.nodeConfigs.function.language_detection || canvas.nodeConfigs.function.intent_classification || canvas.nodeConfigs.function.attachment_processing)) {
              nc.preprocess = { ...prev.preprocess, ...canvas.nodeConfigs.function };
              nc.function = {};
            }
            // Migrate legacy: condition.input_guardrails / output_guardrails → top-level
            if (!canvas.nodeConfigs.input_guardrails && canvas.nodeConfigs.condition?.input_guardrails) {
              nc.input_guardrails = { ...prev.input_guardrails, ...canvas.nodeConfigs.condition.input_guardrails };
            }
            if (!canvas.nodeConfigs.output_guardrails && canvas.nodeConfigs.condition?.output_guardrails) {
              nc.output_guardrails = { ...prev.output_guardrails, ...canvas.nodeConfigs.condition.output_guardrails };
            }
            if (canvas.nodeConfigs.condition?.input_guardrails || canvas.nodeConfigs.condition?.output_guardrails) {
              nc.condition = {};
            }
            return nc;
          });
        }
        if (canvas.config) {
          const cfg = canvas.config;
          // Pick high level cols always from agentDB not canvas.config
          setTemperature(agent.temperature ?? 0.7);
          setSystemPrompt(agent.system_prompt || "");
          setLlmProvider(agent.provider || "");
          setLlmModel(agent.model || "");
          setLlmMaxTokens(agent.max_tokens || 2048);
          // Pure UI layout state — no equivalent top-level column, canvas is correct here.
          if (cfg.triggerChannels) setTriggerChannels(cfg.triggerChannels);
          if (cfg.triggerName) setTriggerName(cfg.triggerName);
          if (cfg.selectedWebhookId) setSelectedWebhookId(cfg.selectedWebhookId);
        }
      } else {
        // No canvas data — build default canvas from agent properties.
        //
        // v13 Supervisor bridge: when the agent is a supervisor, hydrate
        // the canvas from the matching ORCHESTRATION_TEMPLATES entry
        // (sequential-chain / fan-out-fan-in / conditional-routing /
        // supervisor) and dynamically scale the specialist node count
        // to match the family's actual experts via /api/agents/<id>/tree.
        // The previous path read flow_config.supervisor.experts as a
        // list of NAMES — but it's a list of UUIDs, so labels rendered
        // as IDs and any experts beyond slot #2 in the template were
        // silently dropped.
        const supervisorTemplate = pickTemplateForLoadedSupervisor(agent);
        if (supervisorTemplate) {
          // Show the template's stock canvas immediately (responsive),
          // then upgrade asynchronously once /tree resolves with the
          // real expert names + ids.
          setNodes(populateSupervisorIdentity(supervisorTemplate.nodes, agent));
          setConnections(supervisorTemplate.connections.map(c => ({ ...c })));
          setNodeCounter(supervisorTemplate.nodes.length + 1);
          // Fire-and-forget tree fetch → rebuild canvas with real experts.
          agentApi.getTree(agent.id).then(treeRes => {
            captureTreeBaseline(treeRes);  // D9: record loaded expert/workflow ids
            const experts = treeRes?.data?.experts || [];
            const built = hydrateSupervisorCanvasFromTree({
              template: supervisorTemplate, experts,
              mode: agent.flow_config?.supervisor?.mode,
              agent,
            });
            if (!built) return;
            fitPendingRef.current = true;  // re-fit against the real experts
            setNodes(built.nodes);
            setConnections(built.connections);
            setNodeCounter(built.nodes.length + 1);
          }).catch(() => { /* keep stock template on /tree failure */ });
        } else {
          // No saved canvas + not a supervisor → build the canvas
          // dynamically from flow_config so the user sees what the
          // agent ACTUALLY does (knowledge / api / memory / etc.
          // toggles reflected) instead of the legacy 9-node default.
          const dyn = buildRegularCanvas(agent);
          setNodes(dyn.nodes);
          setConnections(dyn.connections);
          setNodeCounter(dyn.nodes.length + 1);
        }
        setZoom(100);
        setSystemPrompt(agent.system_prompt || "");
        setLlmProvider(agent.provider || "");
        setLlmModel(agent.model || "");
        setLlmMaxTokens(agent.max_tokens || 2048);
        setTemperature(agent.temperature ?? 0.7);
        // rankings + voice are restored AFTER the if/else canvas block
        // (BUG-021): they live in agent.flow_config but NOT in
        // canvas.config, so the canvas-branch save→reload would have
        // skipped them. Moving here would be a duplicate; canonical
        // restore lives after line 861.
        const ch = agent.channels || [];
        const hasWebhook = agent.flow_config?.trigger?.webhook?.enabled || false;
        setTriggerChannels({ whatsapp: ch.includes("whatsapp"), voice: ch.includes("voice"), chat: ch.includes("chat"), sms: ch.includes("sms"), email: ch.includes("email"), webhook: hasWebhook });
        setTriggerName(agent.name || "");
        if (agent.flow_config?.trigger?.webhook?.webhook_id) {
          setSelectedWebhookId(agent.flow_config.trigger.webhook.webhook_id);
        }
        // Restore nodeConfigs from flow_config if available
        if (agent.flow_config) {
          const fc = agent.flow_config;
          setNodeConfigs(prev => ({
            ...prev,
            ...(fc.knowledge && { knowledge: { ...prev.knowledge, ...fc.knowledge } }),
            ...(fc.action && { action: { ...prev.action, ...fc.action, handoff: { ...prev.action.handoff, ...(fc.action.handoff || {}) , whatsapp: { ...prev.action.handoff.whatsapp, ...(fc.action.handoff?.whatsapp || {}) }, chat: { ...prev.action.handoff.chat, ...(fc.action.handoff?.chat || {}) }, voice: { ...prev.action.handoff.voice, ...(fc.action.handoff?.voice || {}) } } } }),
            ...(fc.api && { api: fc.api }),
            ...(fc.memory && { memory: fc.memory }),
            // Guardrails: new top-level keys, fall back to legacy condition.*
            input_guardrails: fc.input_guardrails ? { ...prev.input_guardrails, ...fc.input_guardrails }
              : fc.condition?.input_guardrails ? { ...prev.input_guardrails, ...fc.condition.input_guardrails }
              : prev.input_guardrails,
            output_guardrails: fc.output_guardrails ? { ...prev.output_guardrails, ...fc.output_guardrails }
              : fc.condition?.output_guardrails ? { ...prev.output_guardrails, ...fc.condition.output_guardrails }
              : prev.output_guardrails,
            condition: {},
            // Preprocess: fall back to legacy function config
            preprocess: fc.preprocess ? { ...prev.preprocess, ...fc.preprocess }
              : fc.function ? { ...prev.preprocess, ...fc.function }
              : prev.preprocess,
            function: {},
          }));
        }
      }
      // BUG-025 — flow_config is the canonical config that BOTH the Builder
      // and the Agent Detail page write; metadata_json.canvas.nodeConfigs is a
      // Builder-only snapshot the detail page never updates. The `if (canvas)`
      // branch above restores nodeConfigs SOLELY from that snapshot, so a
      // detail-page edit (e.g. flow_config.knowledge.top_k on the Overview
      // card / Parallel Prepare drawer) was invisible in the Builder — it kept
      // showing its own last-saved value. Like the BUG-021 rankings/voice fix,
      // overlay the canonical flow_config slices UNCONDITIONALLY so flow_config
      // wins per-field. Only 1:1-mapped slices are overlaid — UI-only keys
      // (internal_tools, llm_smart_routing) and the legacy condition/function
      // migrations stay owned by the canvas restore above, so this never
      // resets them. Merge per-field (never whole-slice replace) to preserve
      // any canvas-only sub-keys within a slice.
      const _fc = agent.flow_config;
      if (_fc) {
        setNodeConfigs(prev => ({
          ...prev,
          ...(_fc.knowledge         && { knowledge:         { ...prev.knowledge,         ..._fc.knowledge } }),
          ...(_fc.memory            && { memory:            { ...prev.memory,            ..._fc.memory } }),
          ...(_fc.fact_checker      && { fact_checker:      { ...prev.fact_checker,      ..._fc.fact_checker } }),
          ...(_fc.input_guardrails  && { input_guardrails:  { ...prev.input_guardrails,  ..._fc.input_guardrails } }),
          ...(_fc.output_guardrails && { output_guardrails: { ...prev.output_guardrails, ..._fc.output_guardrails } }),
          ...(_fc.preprocess        && { preprocess:        { ...prev.preprocess,        ..._fc.preprocess } }),
          ...(_fc.grounding         && { grounding:         { ...prev.grounding,         ..._fc.grounding } }),
          ...(_fc.action            && { action: { ...prev.action, ..._fc.action, handoff: { ...prev.action.handoff, ...(_fc.action.handoff || {}), whatsapp: { ...prev.action.handoff.whatsapp, ...(_fc.action.handoff?.whatsapp || {}) }, chat: { ...prev.action.handoff.chat, ...(_fc.action.handoff?.chat || {}) }, voice: { ...prev.action.handoff.voice, ...(_fc.action.handoff?.voice || {}) } } } }),
        }));
      }
      // BUG-021 — flow_config.llm.rankings + flow_config.voice live in
      // agent.flow_config but are NOT carried in canvas.config. The pre-
      // fix code restored them inside the `else (no-canvas)` branch only,
      // so any Builder-saved agent (which always has canvas) silently
      // reverted them to React useState defaults. User-visible symptom:
      // Aanya Fallback toggle reads OFF after save → user re-toggles ON
      // → saves → reloads → OFF again ad infinitum. Voice config has the
      // same bug but is silent (defaults look "normal" and a subsequent
      // save can overwrite previously-tuned voice with the React defaults).
      // Fix: restore these unconditionally on every load.
      if (agent.flow_config?.llm?.rankings?.length) {
        setLlmRankingsMode(true);
        setLlmRankings(agent.flow_config.llm.rankings);
      } else {
        setLlmRankingsMode(false);
        setLlmRankings([]);
      }
      if (agent.flow_config?.voice) {
        setVoiceConfig(prev => ({ ...prev, ...agent.flow_config.voice }));
      }
      setCurrentAgentId(agent.id);
      treeVersionRef.current = Number(agent.tree_version ?? 0);  // D3 concurrency token
      setAgentName(agent.name);
      setAgentVersion(agent.version || "v1.0");
      setRoutingIntents(agent.routing_intents || []);
      setRoutingConfig(agent.flow_config?.supervisor?.routing || null);
      setIsDefault(agent.is_default || false);
      setIsDynamicAgent(agent.metadata_json?.is_dynamic_agent || false);
      setAgentPriority(agent.priority ?? 100);
      setSelectedNode(null);
      // C10 — restore custom node labels from flow_config.canvas.node_labels.
      const labels = agent.flow_config?.canvas?.node_labels;
      if (labels && typeof labels === "object") {
        setCustomNodeLabels({ ...labels });
      } else {
        setCustomNodeLabels({});
      }
      // C9 — append integration + workflow nodes from server bindings.
      // Also pass saved canvas so orphan integration/workflow nodes (those
      // whose binding got soft-deleted) survive the re-hydrate.
      _appendBindingsForAgent(agent.id, agent.metadata_json?.canvas?.nodes);
      // Only clear pendingEditAgent if it was the entry path (prop
      // route). For deep-link path, no Shell-side state to clear —
      // the URL itself is the source of truth.
      if (pendingEditAgent) {
        clearPendingEdit();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEditAgent, deepLinkAgent]);

  // Agent-level LLM fields (model/max_tokens/temperature) are persisted by the
  // main Save Agent flow, not here. The Save button in the LLM panel is gone
  // because it used to push agent-builder state into the tenant-wide OpenAI
  // defaults — wrong layer. Test Connection still works for the API key.

  const handleLlmTest = async () => {
    setLlmTesting(true);
    setLlmTestResult(null);
    try {
      const res = await authApi.testLlmConfig();
      setLlmTestResult({ success: true, model: res.data.model, reply: res.data.reply });
    } catch (err) {
      setLlmTestResult({ success: false, error: err.message || "Connection failed" });
    } finally { setLlmTesting(false); }
  };


  // v13 Supervisor bridge — runs at save time. When the canvas expresses
  // a supervisor pattern (sequential / fan-out / conditional / supervisor
  // template), `inferSupervisorShape` returns a non-null shape and we
  // flip `agent_kind` + write `flow_config.supervisor.{mode,experts,...}`
  // before the API call. See ./supervisorBridge.js for the inference
  // logic + canvas-template-id → mode mapping.
  //
  // Specialist-channel warnings are collected for the caller to surface
  // (non-blocking — they do NOT stop the save). Auto-clearing the
  // specialists' channels is OUT OF SCOPE here — the specialist agent's
  // own save handler should do that on its next save.
  const buildSavePayload = (name, version) => {
    const channels = Object.entries(triggerChannels).filter(([k, v]) => v && k !== "webhook").map(([k]) => k);
    const canvasState = {
      nodes, connections, zoom, nodeCounter, nodeConfigs,
      config: { temperature, systemPrompt, llmProvider, llmModel, llmMaxTokens, triggerChannels, triggerName, agentName: name, agentVersion: version, selectedWebhookId },
    };
    const agentNodes = nodes.filter(n => n.type === "agent" && n.agentConfig?.agentId);
    const flowConfig = {
      trigger: {
        channels, name: triggerName || name,
        ...(triggerChannels.webhook && selectedWebhookId && {
          webhook: { enabled: true, webhook_id: selectedWebhookId },
        }),
      },
      // BUG-020: the Ranked Fallback toggle's OFF state must be sent
      // explicitly (rankings: null) so the backend's _deep_merge clears
      // the persisted array. Conditional-spread `...(toggleMode && len)`
      // omits the field, which deep-merge treats as "no change" — the
      // stale rankings stay, and load re-infers the toggle as ON.
      llm: {
        provider: llmProvider, model: llmModel, temperature,
        max_tokens: llmMaxTokens, system_prompt: systemPrompt,
        rankings: (llmRankingsMode && llmRankings.length > 0) ? llmRankings : null,
        ...(nodeConfigs.llm_smart_routing?.enabled && { smart_routing: nodeConfigs.llm_smart_routing }),
      },
      knowledge: { ...nodeConfigs.knowledge },
      input_guardrails: { ...nodeConfigs.input_guardrails },
      output_guardrails: { ...nodeConfigs.output_guardrails },
      fact_checker: { ...nodeConfigs.fact_checker },
      condition: { ...nodeConfigs.condition },
      action: { ...nodeConfigs.action },
      api: {
        ...nodeConfigs.api,
        // Always emit `internal_toolsets` so the backend deep-merge sees
        // the user's current intent. Without this, unchecking all
        // toolsets (or toggling internal_tools off) was a silent no-op
        // — the previous list survived the merge and the agent kept
        // calling tools the user thought they'd disabled.
        internal_toolsets: (nodeConfigs.internal_tools?.enabled && Array.isArray(nodeConfigs.internal_tools?.toolsets))
          ? nodeConfigs.internal_tools.toolsets
          : [],
      },
      memory: { ...nodeConfigs.memory },
      ...(voiceConfig.voice_id && { voice: { ...voiceConfig } }),
      preprocess: { ...nodeConfigs.preprocess },
      function: { ...nodeConfigs.function },
      grounding: { ...nodeConfigs.grounding },
      // C10 — custom node labels keyed by node.id. Viewer's GraphTab
      // (B8) overlays these onto the static node catalog so a renamed
      // "LLM Engine" → "DocuSign Orchestrator" shows up everywhere.
      ...(Object.keys(customNodeLabels).length > 0 && {
        canvas: { node_labels: { ...customNodeLabels } },
      }),
      ...(agentNodes.length > 0 && {
        orchestration: {
          type: agentNodes.length === 1 ? "sequential" : (agentNodes.some(n => n.agentConfig?.executionMode === "parallel") ? "parallel" : "sequential"),
          steps: agentNodes.map(n => ({
            nodeId: n.id, agentId: n.agentConfig.agentId,
            executionMode: n.agentConfig.executionMode,
            inputMapping: n.agentConfig.inputMapping,
            outputHandling: n.agentConfig.outputHandling,
            timeout_ms: n.agentConfig.timeout_ms,
            errorHandling: n.agentConfig.errorHandling,
          })),
        },
      }),
    };
    const payload = {
      name, version, channels, provider: llmProvider, model: llmModel, temperature, max_tokens: llmMaxTokens,
      system_prompt: systemPrompt, status: "draft",
      flow_config: flowConfig,
      metadata_json: { canvas: canvasState, ...(isDynamicAgent && { is_dynamic_agent: true }) },
      routing_intents: routingIntents,
      is_default: isDefault,
      priority: agentPriority,
    };

    // ── v13 Supervisor bridge ─────────────────────────────────────────
    // If the canvas looks like a supervisor pattern, flip agent_kind +
    // write flow_config.supervisor.{mode, experts, max_iterations} on
    // top of whatever existing supervisor block was previously persisted
    // (so we preserve auto-tuned exemplars / routing thresholds).
    const warnings = [];
    const shape = inferSupervisorShape(nodes, connections);
    if (shape) {
      const patch = buildSupervisorPatch({ shape, agentName: name, routing: routingConfig });
      payload.agent_kind = patch.agent_kind;

      // Read the existing supervisor block off the loaded agent (if any)
      // — it's not on local state, so we look at metadata_json.canvas if
      // present; if absent, this is a fresh canvas and we just author it
      // fresh. Caller (the API) treats this as a partial update on
      // flow_config.supervisor anyway.
      const existingSupervisor =
        (pendingEditAgent?.flow_config?.supervisor) || {};
      payload.flow_config.supervisor = {
        ...existingSupervisor,
        ...patch.supervisor,
      };

      // Specialist-channel warning. If a specialist node is bound to a
      // real agent and that target agent currently has channels, emit a
      // non-blocking toast warning. We do NOT auto-clear the channels
      // here — that's the specialist agent's own save handler's job.
      const specialistsByName = new Map();
      for (const expertNode of nodes) {
        if (
          !expertNode ||
          expertNode.type !== "agent" ||
          expertNode.agentConfig?.executionMode === "supervisor"
        ) continue;
        const boundId = expertNode.agentConfig?.agentId;
        if (!boundId) continue;
        const target = (availableAgents || []).find((a) => a.id === boundId);
        if (!target) continue;
        const targetChannels = target.channels || [];
        if (targetChannels.length > 0) {
          specialistsByName.set(
            target.name || expertNode.agentConfig?.agentName || expertNode.label || "specialist",
            targetChannels,
          );
        }
      }
      for (const [specName, chs] of specialistsByName) {
        warnings.push(
          `Specialist ${specName} has channels [${chs.join(", ")}] — these will be cleared on the next save of ${specName} so it doesn't compete in inbound routing.`,
        );
      }
    }

    return { payload, warnings };
  };

  // Helper: surface the (possibly empty) warning list as a follow-up
  // toast after the success toast. Non-blocking — warnings ride after
  // the success message disappears.
  const _surfaceSaveWarnings = (warnings) => {
    if (!warnings || warnings.length === 0) return;
    // Show warnings 3.2s later (after the success toast clears at 3s),
    // and keep them on screen for 7s so the admin can read them.
    setTimeout(() => {
      setSaveResult({ success: false, error: warnings.join(" ") });
      setTimeout(() => setSaveResult(null), 7000);
    }, 3200);
  };

  // ASC-v2 (F1/F2/F3): ONE canvas→/tree body builder, shared by ALL THREE save
  // entrypoints (Save / Save New / Save As). The two create handlers previously
  // skipped this entirely (create-only), so a brand-new supervisor authored on
  // the canvas dropped its experts on first save. Centralizing the serialization
  // guarantees every entrypoint sends the same experts / bindings / removed-diff.
  const buildCanvasTreeBody = (bindingAgentId, payload, expectedTreeVersion) => {
    const integrationsAndWorkflows = inferIntegrationsAndWorkflows(nodes, connections, bindingAgentId);
    const unsavedExperts = collectUnsavedExpertPayloads(nodes);
    const currentExpertIds = collectSavedExpertIds(nodes);
    const currentWorkflowIds = new Set(
      (integrationsAndWorkflows.workflows || []).map((w) => w.workflow_id).filter(Boolean)
    );
    const removedExpertIds = computeRemovedIds(loadedExpertIdsRef.current, currentExpertIds);
    const removedWorkflowIds = computeRemovedIds(loadedWorkflowIdsRef.current, currentWorkflowIds);
    const treeBody = buildTreeSavePayload({
      payload, unsavedExperts, integrationsAndWorkflows,
      expectedTreeVersion, removedExpertIds, removedWorkflowIds,
    });
    return { treeBody, integrationsAndWorkflows };
  };

  // ASC-v2 (F1/F2): a NEW supervisor is created via the ATOMIC create-tree
  // endpoint — the row AND its canvas experts/bindings/workflows land in ONE
  // transaction, so there's no create-succeeds-then-putTree-fails window (and
  // apply_tree/B1 owns the kind). Regular agents need no tree; callers use plain
  // create. Body: { agent, experts, integrations, workflows }.
  const buildCreateTreeBody = (payload) => {
    const iw = inferIntegrationsAndWorkflows(nodes, connections, null);
    return {
      agent: payload,
      experts: collectUnsavedExpertPayloads(nodes),
      integrations: {
        mcp_server_bindings: iw.mcp_server_bindings,
        openapi_integration_bindings: iw.openapi_integration_bindings,
      },
      workflows: iw.workflows,
    };
  };

  // C14 — Save flips to PUT /api/agents/:id/tree (atomic Builder save).
  // Single HTTP request now writes the supervisor row + integration
  // bindings + workflows in one transaction (per agent-builder-v1.md §7).
  // C15 backward compat: regular agents save through the same endpoint
  // with empty experts / bindings / workflows arrays — only the
  // supervisor block is updated.
  const handleSaveAgent = async () => {
    if (!currentAgentId) {
      setSaveNewName(agentName === "Untitled Agent" ? "" : agentName);
      setSaveNewVersion(agentVersion);
      setShowSaveNewModal(true);
      return;
    }
    const name = agentName.trim() || "Untitled Agent";
    setSaving(true);
    setSaveResult(null);
    try {
      const { payload, warnings } = buildSavePayload(name, agentVersion);
      // Convert single-agent payload → /tree shape per §7.1.
      // currentAgentId is the binding target of last resort — a regular
      // agent's canvas has no agentId on its LLM node, and without it
      // apply_tree would receive an empty binding set and soft-delete
      // every live integration binding on this agent.
      // ASC-v2 (F3): shared serializer — experts (incl. ExpertWizard-authored
      // canvas nodes with no agentId → apply_tree INSERT branch), integration
      // bindings, workflows, the removed-diff (D9), and expected_tree_version
      // (D3). currentAgentId is the binding target of last resort so a regular
      // agent's bindings aren't soft-deleted.
      const { treeBody, integrationsAndWorkflows } = buildCanvasTreeBody(
        currentAgentId, payload, treeVersionRef.current,
      );
      // A supervisor canvas save is a TWO-step write. `/tree` (apply_tree) is
      // supervisor-anchored — it upserts experts + normalizes the roster but
      // does NOT set the row's `agent_kind`. So we first seed the supervisor
      // row (agent_kind transition + scalar fields: prompt/channels/model/
      // priority/routing_intents/…) via the single-row PUT, THEN persist the
      // canvas experts + integrations + roster through the atomic tree save.
      //
      // BUGFIX (supervisor experts vanish on save): the old code STOPPED at the
      // single-row PUT for any `agent_kind !== "regular"` save, so newly
      // authored canvas experts (collected into `unsavedExperts`/`treeBody`)
      // were never sent to `/tree` — never created as agent rows, and their
      // names never resolved into `flow_config.supervisor.experts`. They
      // vanished on every save. `putTree` must run for supervisors too.
      let res;
      if (payload.agent_kind && payload.agent_kind !== "regular") {
        // 1) seed/persist the supervisor row itself (kind + scalar fields).
        await agentApi.update(currentAgentId, payload);
        // 2) create the canvas experts + bindings + normalize the roster.
        res = await agentApi.putTree(currentAgentId, treeBody);
      } else {
        res = await agentApi.putTree(currentAgentId, treeBody);
      }
      if (res?.success) {
        // D3: adopt the server's new tree_version so a second save this
        // session isn't falsely rejected as stale.
        const newTV = res.data?.supervisor?.tree_version;
        if (typeof newTV === "number") treeVersionRef.current = newTV;
        // D9: the just-removed experts/workflows are gone now — refresh the
        // baseline to the surviving canvas set so a second save this session
        // doesn't try to remove them again.
        loadedExpertIdsRef.current = collectSavedExpertIds(nodes);
        loadedWorkflowIdsRef.current = new Set(
          (integrationsAndWorkflows.workflows || []).map((w) => w.workflow_id).filter(Boolean)
        );
        const audit = res.data?.save_audit;
        const auditNote = audit ? ` (\u0394 +${audit.rows_inserted}/~${audit.rows_updated}/-${audit.rows_soft_deleted})` : "";
        setSaveResult({ success: true, message: `Agent "${name}" saved${auditNote}` });
        setTimeout(() => setSaveResult(null), 3000);
        _surfaceSaveWarnings(warnings);
      }
    } catch (err) {
      const detail = err?.body?.detail;
      let msg = err.message || "Failed to save";
      if (typeof detail === "object" && detail?.error === "stale_tree_version") {
        msg = "Stale canvas — another tab saved first. Reload to fetch the latest.";
      } else if (typeof detail === "object" && detail?.error === "orphan_integration_nodes") {
        msg = `Floating integration node(s): ${(detail.orphans || []).map(o => o.node || o.kind).join(", ")}`;
      }
      setSaveResult({ success: false, error: msg });
    } finally { setSaving(false); }
  };

  const handleSaveNewAgent = async () => {
    const name = saveNewName.trim();
    if (!name) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const { payload, warnings } = buildSavePayload(name, saveNewVersion);
      // ASC-v2 (F1): a NEW supervisor is created ATOMICALLY (row + experts +
      // roster in one transaction) — no create-then-putTree window. A regular
      // agent has no tree, so it uses plain create.
      const isSupervisor = payload.agent_kind && payload.agent_kind !== "regular";
      const res = isSupervisor
        ? await agentApi.createTree(buildCreateTreeBody(payload))
        : await agentApi.create(payload);
      if (res.success) {
        const newId = res.data.id;
        setCurrentAgentId(newId);
        setAgentName(name);
        setAgentVersion(saveNewVersion);
        if (isSupervisor) {
          const newTV = res.data?.supervisor?.tree_version;
          if (typeof newTV === "number") treeVersionRef.current = newTV;
          loadedExpertIdsRef.current = collectSavedExpertIds(nodes);
        }
        setSaveResult({ success: true, message: `Agent "${name}" created` });
        setTimeout(() => setSaveResult(null), 3000);
        setShowSaveNewModal(false);
        _surfaceSaveWarnings(warnings);
      }
    } catch (err) {
      setSaveResult({ success: false, error: err.message || "Failed to save" });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAsAgent = async () => {
    const name = saveAsName.trim();
    if (!name) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const { payload, warnings } = buildSavePayload(name, saveAsVersion);

      // Supervisor pattern: require a supervisor agent to be selected before saving.
      // Copy its LLM config + channels so the new wrapper doesn't send empty fields.
      if (payload.agent_kind === "supervisor") {
        const supervisorNode = nodes.find(
          n => n.type === "agent" && n.agentConfig?.executionMode === "supervisor"
        );
        const selectedSupId = supervisorNode?.agentConfig?.agentId;
        if (!selectedSupId) {
          setSaveResult({ success: false, error: "Please select a Supervisor agent on the canvas before saving this pattern." });
          return;
        }
        const supAgent = (availableAgents || []).find(a => a.id === selectedSupId);
        if (supAgent) {
          // ASC-v2 (F4): BYOK-strict — copy the selected supervisor's provider/
          // model, falling back to the payload's own resolved values, never a
          // hardcoded "openai" literal. Empty is legitimate (the create endpoint's
          // BYOK gate + model_resolver pick the tenant default).
          payload.provider      = supAgent.provider      || payload.provider;
          payload.model         = supAgent.model         || payload.model;
          payload.temperature   = supAgent.temperature   ?? payload.temperature;
          payload.max_tokens    = supAgent.max_tokens    ?? payload.max_tokens;
          payload.system_prompt = supAgent.system_prompt || payload.system_prompt;
          if (Array.isArray(supAgent.channels) && supAgent.channels.length > 0) {
            payload.channels = supAgent.channels;
          }
          if (payload.flow_config?.llm) {
            payload.flow_config.llm.provider      = payload.provider;
            payload.flow_config.llm.model         = payload.model;
            payload.flow_config.llm.temperature   = payload.temperature;
            payload.flow_config.llm.max_tokens    = payload.max_tokens;
            payload.flow_config.llm.system_prompt = payload.system_prompt;
          }
        }
      }

      // ASC-v2 (F2): Save-As of a supervisor is ATOMIC (row + experts + roster in
      // one transaction); a regular uses plain create.
      const isSupervisor = payload.agent_kind && payload.agent_kind !== "regular";
      const res = isSupervisor
        ? await agentApi.createTree(buildCreateTreeBody(payload))
        : await agentApi.create(payload);
      if (res.success) {
        const newId = res.data.id;
        setCurrentAgentId(newId);
        setAgentName(name);
        setAgentVersion(saveAsVersion);
        if (isSupervisor) {
          const newTV = res.data?.supervisor?.tree_version;
          if (typeof newTV === "number") treeVersionRef.current = newTV;
          loadedExpertIdsRef.current = collectSavedExpertIds(nodes);
        }
        setSaveResult({ success: true, message: `Agent "${name}" saved as new` });
        setTimeout(() => setSaveResult(null), 3000);
        _surfaceSaveWarnings(warnings);
      }
    } catch (err) {
      setSaveResult({ success: false, error: err.message || "Failed to save" });
    } finally {
      setSaving(false);
      setShowSaveAsModal(false);
    }
  };

  const handleLoadAgentsList = async () => {
    setShowLoadModal(true);
    setLoadingAgents(true);
    try {
      const res = await agentApi.list();
      if (res.success) setSavedAgents(res.data || []);
    } catch {} finally { setLoadingAgents(false); }
  };

  const handleLoadAgent = (agent) => {
    saveUndo();
    const canvas = agent.metadata_json?.canvas;
    // BUG-024 self-heal — supervisor positions ALWAYS re-derive from
    // /tree; saved canvas only contributes configs. See sibling logic
    // in the pendingEditAgent useEffect for the full rationale.
    const stalePositions = (
      agent.agent_kind === "supervisor" && canvas?.nodes
    );
    fitPendingRef.current = true;
    if (canvas) {
      if (stalePositions) {
        const supervisorTemplate = pickTemplateForLoadedSupervisor(agent);
        if (supervisorTemplate) {
          setNodes(populateSupervisorIdentity(supervisorTemplate.nodes, agent));
          setConnections(supervisorTemplate.connections.map(c => ({ ...c })));
          setNodeCounter(supervisorTemplate.nodes.length + 1);
          agentApi.getTree(agent.id).then(treeRes => {
            captureTreeBaseline(treeRes);  // D9: record loaded expert/workflow ids
            const built = hydrateSupervisorCanvasFromTree({
              template: supervisorTemplate,
              experts: treeRes?.data?.experts || [],
              mode: agent.flow_config?.supervisor?.mode,
              agent,
            });
            if (!built) return;
            fitPendingRef.current = true;  // re-fit against the real experts
            setNodes(built.nodes);
            setConnections(built.connections);
            setNodeCounter(built.nodes.length + 1);
            // Orphan-canvas preservation runs in _appendBindingsForAgent.
          }).catch(() => {});
        } else if (canvas.nodes) {
          setNodes(canvas.nodes);
          setConnections(canvas.connections);
        }
        setZoom(100);
      } else {
        if (canvas.nodes) setNodes(canvas.nodes);
        if (canvas.connections) setConnections(canvas.connections);
        if (canvas.zoom) setZoom(canvas.zoom);
        if (canvas.nodeCounter) setNodeCounter(canvas.nodeCounter);
      }
      if (canvas.config) {
        const cfg = canvas.config;
        // LLM fields come exclusively from the live agent DB row — canvas.config is a
        // UI snapshot from the last builder save and can be stale (detail-page edits
        // update agents.temperature/model/etc. but never touch the snapshot).
        // AgentDB columns always have defaults, so agent.* is never absent.
        setTemperature(agent.temperature ?? 0.7);
        setSystemPrompt(agent.system_prompt || "");
        setLlmProvider(agent.provider || "");
        setLlmModel(agent.model || "");
        setLlmMaxTokens(agent.max_tokens || 2048);
        // Pure UI layout state — no equivalent top-level column, canvas is correct here.
        if (cfg.triggerChannels) setTriggerChannels(cfg.triggerChannels);
        if (cfg.triggerName) setTriggerName(cfg.triggerName);
        if (cfg.selectedWebhookId) setSelectedWebhookId(cfg.selectedWebhookId);
      }
      if (canvas.nodeConfigs) {
        setNodeConfigs(prev => {
          const nc = { ...prev, ...canvas.nodeConfigs };
          // Migrate legacy: function → preprocess
          if (!canvas.nodeConfigs.preprocess && canvas.nodeConfigs.function &&
              (canvas.nodeConfigs.function.language_detection || canvas.nodeConfigs.function.intent_classification || canvas.nodeConfigs.function.attachment_processing)) {
            nc.preprocess = { ...prev.preprocess, ...canvas.nodeConfigs.function };
            nc.function = {};
          }
          // Migrate legacy: condition.input/output_guardrails → top-level
          if (!canvas.nodeConfigs.input_guardrails && canvas.nodeConfigs.condition?.input_guardrails) {
            nc.input_guardrails = { ...prev.input_guardrails, ...canvas.nodeConfigs.condition.input_guardrails };
          }
          if (!canvas.nodeConfigs.output_guardrails && canvas.nodeConfigs.condition?.output_guardrails) {
            nc.output_guardrails = { ...prev.output_guardrails, ...canvas.nodeConfigs.condition.output_guardrails };
          }
          if (canvas.nodeConfigs.condition?.input_guardrails || canvas.nodeConfigs.condition?.output_guardrails) {
            nc.condition = {};
          }
          return nc;
        });
      } else if (agent.flow_config) {
        const fc = agent.flow_config;
        setNodeConfigs(prev => ({
          ...prev,
          ...(fc.knowledge && { knowledge: { ...prev.knowledge, ...fc.knowledge } }),
          ...(fc.action && { action: { ...prev.action, ...fc.action, handoff: { ...prev.action.handoff, ...(fc.action.handoff || {}), whatsapp: { ...prev.action.handoff.whatsapp, ...(fc.action.handoff?.whatsapp || {}) }, chat: { ...prev.action.handoff.chat, ...(fc.action.handoff?.chat || {}) }, voice: { ...prev.action.handoff.voice, ...(fc.action.handoff?.voice || {}) } } } }),
          ...(fc.api && { api: { ...prev.api, ...fc.api } }),
          ...(fc.memory && { memory: { ...prev.memory, ...fc.memory } }),
          input_guardrails: fc.input_guardrails ? { ...prev.input_guardrails, ...fc.input_guardrails }
            : fc.condition?.input_guardrails ? { ...prev.input_guardrails, ...fc.condition.input_guardrails }
            : prev.input_guardrails,
          output_guardrails: fc.output_guardrails ? { ...prev.output_guardrails, ...fc.output_guardrails }
            : fc.condition?.output_guardrails ? { ...prev.output_guardrails, ...fc.condition.output_guardrails }
            : prev.output_guardrails,
          condition: {},
          preprocess: fc.preprocess ? { ...prev.preprocess, ...fc.preprocess }
            : fc.function ? { ...prev.preprocess, ...fc.function }
            : prev.preprocess,
          function: {},
        }));
      }
    } else {
      // No canvas data — restore from top-level agent fields with default nodes.
      //
      // v13 Supervisor bridge: prefer the matching orchestration template
      // when the agent is a supervisor. See the equivalent branch near
      // line ~217 for the full rationale.
      const supervisorTemplate = pickTemplateForLoadedSupervisor(agent);
      if (supervisorTemplate) {
        // Stock template first (responsive), then upgrade async with
        // real experts from /tree. See the pendingEditAgent path
        // (~line 376) for the rationale — flow_config.supervisor.experts
        // holds UUIDs, not names, and the template's specialist count
        // is fixed at 2 regardless of family size.
        setNodes(populateSupervisorIdentity(supervisorTemplate.nodes, agent));
        setConnections(supervisorTemplate.connections.map(c => ({ ...c })));
        setNodeCounter(supervisorTemplate.nodes.length + 1);
        agentApi.getTree(agent.id).then(treeRes => {
          captureTreeBaseline(treeRes);  // D9: record loaded expert/workflow ids
          const experts = treeRes?.data?.experts || [];
          const built = hydrateSupervisorCanvasFromTree({
            template: supervisorTemplate, experts,
            mode: agent.flow_config?.supervisor?.mode,
            agent,
          });
          if (!built) return;
          fitPendingRef.current = true;  // re-fit against the real experts
          setNodes(built.nodes);
          setConnections(built.connections);
          setNodeCounter(built.nodes.length + 1);
        }).catch(() => { /* keep stock template on /tree failure */ });
      } else {
        // Regular agent + no saved canvas → build from flow_config so
        // the canvas reflects what the agent ACTUALLY does (knowledge /
        // memory / api / grounding toggles), not the legacy 9-node
        // default. See features/builder/regularCanvasFromFlow.js.
        const dyn = buildRegularCanvas(agent);
        setNodes(dyn.nodes);
        setConnections(dyn.connections);
        setNodeCounter(dyn.nodes.length + 1);
      }
      setZoom(100);
      if (agent.temperature !== undefined) setTemperature(agent.temperature);
      if (agent.system_prompt) setSystemPrompt(agent.system_prompt);
      if (agent.provider) setLlmProvider(agent.provider);
      if (agent.model) setLlmModel(agent.model);
      if (agent.max_tokens) setLlmMaxTokens(agent.max_tokens);
      if (agent.channels) {
        const ch = { whatsapp: false, voice: false, chat: false, sms: false, webhook: false };
        agent.channels.forEach(c => { if (ch.hasOwnProperty(c)) ch[c] = true; });
        if (agent.flow_config?.trigger?.webhook?.enabled) ch.webhook = true;
        setTriggerChannels(ch);
        if (agent.flow_config?.trigger?.webhook?.webhook_id) {
          setSelectedWebhookId(agent.flow_config.trigger.webhook.webhook_id);
        }
      }
      if (agent.flow_config) {
        const fc = agent.flow_config;
        setNodeConfigs(prev => ({
          ...prev,
          ...(fc.knowledge && { knowledge: { ...prev.knowledge, ...fc.knowledge } }),
          ...(fc.action && { action: { ...prev.action, ...fc.action, handoff: { ...prev.action.handoff, ...(fc.action.handoff || {}), whatsapp: { ...prev.action.handoff.whatsapp, ...(fc.action.handoff?.whatsapp || {}) }, chat: { ...prev.action.handoff.chat, ...(fc.action.handoff?.chat || {}) }, voice: { ...prev.action.handoff.voice, ...(fc.action.handoff?.voice || {}) } } } }),
          ...(fc.api && { api: { ...prev.api, ...fc.api } }),
          ...(fc.memory && { memory: { ...prev.memory, ...fc.memory } }),
          input_guardrails: fc.input_guardrails ? { ...prev.input_guardrails, ...fc.input_guardrails }
            : fc.condition?.input_guardrails ? { ...prev.input_guardrails, ...fc.condition.input_guardrails }
            : prev.input_guardrails,
          output_guardrails: fc.output_guardrails ? { ...prev.output_guardrails, ...fc.output_guardrails }
            : fc.condition?.output_guardrails ? { ...prev.output_guardrails, ...fc.condition.output_guardrails }
            : prev.output_guardrails,
          condition: {},
          preprocess: fc.preprocess ? { ...prev.preprocess, ...fc.preprocess }
            : fc.function ? { ...prev.preprocess, ...fc.function }
            : prev.preprocess,
          function: {},
        }));
      }
    }
    // BUG-021 — flow_config.llm.rankings + flow_config.voice were never
    // restored in this handler (handleLoadAgent — the in-session "switch
    // agent" path). Pre-fix, switching to an agent with rankings configured
    // showed the Fallback toggle as OFF, and switching to one with custom
    // voice tuning showed the voice node at React defaults. Both saved
    // correctly to flow_config.* on the way OUT; both were silently
    // discarded on the way IN. Mirror the canonical fix from the
    // pendingEditAgent/deepLinkAgent useEffect — restore unconditionally.
    if (agent.flow_config?.llm?.rankings?.length) {
      setLlmRankingsMode(true);
      setLlmRankings(agent.flow_config.llm.rankings);
    } else {
      setLlmRankingsMode(false);
      setLlmRankings([]);
    }
    if (agent.flow_config?.voice) {
      setVoiceConfig(prev => ({ ...prev, ...agent.flow_config.voice }));
    }
    setCurrentAgentId(agent.id);
    treeVersionRef.current = Number(agent.tree_version ?? 0);  // D3 concurrency token
    setAgentName(agent.name);
    setAgentVersion(agent.version || "v1.0");
    setRoutingIntents(agent.routing_intents || []);
    setRoutingConfig(agent.flow_config?.supervisor?.routing || null);
    setIsDefault(agent.is_default || false);
    setIsDynamicAgent(agent.metadata_json?.is_dynamic_agent || false);
    setAgentPriority(agent.priority ?? 100);
    setShowLoadModal(false);
    setSelectedNode(null);
    // C10 — restore custom node labels.
    const labels = agent.flow_config?.canvas?.node_labels;
    setCustomNodeLabels(labels && typeof labels === "object" ? { ...labels } : {});
    // C9 — append integration + workflow nodes from server bindings.
    _appendBindingsForAgent(agent.id, agent.metadata_json?.canvas?.nodes);
  };

  const saveUndo = () => {
    setUndoStack(prev => [...prev.slice(-19), { nodes: [...nodes], connections: [...connections] }]);
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const last = undoStack[undoStack.length - 1];
    setNodes(last.nodes);
    setConnections(last.connections);
    setUndoStack(prev => prev.slice(0, -1));
  };

  const handleNodeDrag = (id, clientX, clientY) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scale = zoom / 100;
    const scrollLeft = canvasRef.current.scrollLeft;
    const scrollTop = canvasRef.current.scrollTop;
    setNodes(prev => prev.map(n => n.id === id ? {
      ...n,
      x: (clientX - rect.left + scrollLeft) / scale,
      y: (clientY - rect.top + scrollTop) / scale,
    } : n));
  };

  const handleCanvasMouseMove = (e) => {
    // Pan branch — when active, scroll the viewport instead of computing
    // mousePos for the connection-draw rubber band. Mutually exclusive
    // with connection drag (the down-handler refuses to start a pan if
    // a connection is in flight). Only flag didMove past a 5px threshold
    // so a plain click-and-release on empty canvas still falls through
    // to the onClick handler (clear selection).
    if (panState.current.panning) {
      const dx = e.clientX - panState.current.startX;
      const dy = e.clientY - panState.current.startY;
      if (Math.abs(dx) + Math.abs(dy) > 5) {
        panState.current.didMove = true;
      }
      if (panState.current.didMove) {
        e.preventDefault();
        if (canvasRef.current) {
          canvasRef.current.scrollLeft = panState.current.scrollLeft - dx;
          canvasRef.current.scrollTop  = panState.current.scrollTop  - dy;
        }
        return;
      }
    }
    if ((connectingFrom || reconnectDrag) && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const scale = zoom / 100;
      const scrollLeft = canvasRef.current.scrollLeft;
      const scrollTop = canvasRef.current.scrollTop;
      setMousePos({
        x: (e.clientX - rect.left + scrollLeft) / scale,
        y: (e.clientY - rect.top + scrollTop) / scale,
      });
    }
  };

  const handleCanvasMouseUp = () => {
    // Only suppress the trailing onClick when the pan ACTUALLY moved
    // the viewport (didMove gate, 5px threshold). A plain click without
    // drag still falls through to onClick → clear selection / close
    // node config panel.
    if (panState.current.panning && panState.current.didMove) {
      panState.current.justPanned = true;
    }
    panState.current.panning = false;
    panState.current.didMove = false;
    setConnectingFrom(null);
    setReconnectDrag(null);
    setMousePos(null);
  };

  // Click-and-drag on empty canvas pans the viewport. DraggableNode
  // stops propagation on its own mousedown, so canvas-scroll only sees
  // mousedown on the BACKGROUND — which is exactly the gesture for
  // pan. Middle-mouse + shift+left also still work (legacy / power-
  // user habits). Refuses to start while a connection is in flight so
  // the rubber-band wire-drawing doesn't fight the pan.
  const handleCanvasMouseDown = (e) => {
    if (connectingFrom || reconnectDrag) return;
    const isLeft = e.button === 0;
    const isMiddle = e.button === 1;
    if (!isLeft && !isMiddle) return;
    panState.current = {
      panning: true,
      startX: e.clientX, startY: e.clientY,
      scrollLeft: canvasRef.current?.scrollLeft || 0,
      scrollTop:  canvasRef.current?.scrollTop  || 0,
      justPanned: false,
      didMove: false,
    };
  };

  // Ctrl/Cmd+wheel (and trackpad pinch — browsers report it as
  // ctrlKey=true on wheel) zooms; plain wheel falls through to the
  // browser's native vertical scroll on the canvas-scroll container.
  // Floor is 10% so the user can zoom out far enough to see every
  // node in a 50-expert supervisor (BUG-024 user feedback).
  //
  // Attached via useEffect with passive:false. React's onWheel prop
  // is registered as passive by default — preventDefault() inside it
  // logs "Unable to preventDefault inside passive event listener"
  // and silently no-ops, so the browser still scrolls the canvas
  // even when we want to zoom.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -5 : 5;
      setZoom(z => Math.max(10, Math.min(200, z + delta)));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const handlePortMouseDown = (nodeId) => {
    setConnectingFrom(nodeId);
  };

  const handlePortMouseUp = (nodeId) => {
    // Reconnect drag — landing on a new target node
    if (reconnectDrag) {
      const { connIndex, fromNode } = reconnectDrag;
      if (fromNode !== nodeId) {
        const exists = connections.some(c => c.from === fromNode && c.to === nodeId);
        if (!exists) {
          saveUndo();
          setConnections(prev => prev.map((c, i) => i === connIndex ? { ...c, to: nodeId } : c));
        }
      }
      setReconnectDrag(null);
      setSelectedConnection(null);
      setMousePos(null);
      return;
    }
    // Normal new connection
    if (connectingFrom && connectingFrom !== nodeId) {
      const exists = connections.some(c => c.from === connectingFrom && c.to === nodeId);
      if (!exists) {
        saveUndo();
        setConnections(prev => [...prev, { from: connectingFrom, to: nodeId }]);
      }
    }
    setConnectingFrom(null);
    setMousePos(null);
  };

  const handleReconnectStart = (connIndex) => {
    const conn = connections[connIndex];
    if (!conn) return;
    setReconnectDrag({ connIndex, fromNode: conn.from });
    setConnectingFrom(null);
  };

  const handleCanvasDrop = (e) => {
    e.preventDefault();
    setCanvasDragOver(false);
    const paletteType = e.dataTransfer.getData("paletteType");
    if (!paletteType || !canvasRef.current) return;

    const item = PALETTE_ITEMS.find(p => p.type === paletteType);
    if (!item) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const scale = zoom / 100;
    const scrollLeft = canvasRef.current.scrollLeft;
    const scrollTop = canvasRef.current.scrollTop;
    const x = (e.clientX - rect.left + scrollLeft) / scale - 70;
    const y = (e.clientY - rect.top + scrollTop) / scale - 25;
    const newId = `${item.type}-${nodeCounter}`;

    saveUndo();
    const newNode = {
      id: newId, type: item.type, label: item.label,
      icon: item.icon, iconName: item.iconName, color: item.color, x, y,
    };
    if (item.type === "agent") {
      newNode.agentConfig = {
        agentId: null, agentName: null, agentStatus: null,
        executionMode: "sequential",
        inputMapping: { passFullContext: true, customPrompt: "" },
        outputHandling: { mergeStrategy: "replace", outputKey: "agent_response" },
        timeout_ms: 30000,
        errorHandling: { strategy: "fail", fallbackResponse: "" },
      };
    }
    setNodes(prev => [...prev, newNode]);
    setNodeCounter(prev => prev + 1);
    setSelectedNode(newId);

    // §19.6 — when a Sub-Agent is dropped without a backing agent_id,
    // open the inline authoring wizard. Per §5.5 design intent: wizard
    // captures spec into canvas state regardless of whether the parent
    // supervisor has been saved yet — the first Save creates parent +
    // experts together via apply_tree's atomic write.
    if (item.type === "agent") {
      setWizardForNodeId(newId);
      setWizardOpen(true);
    }
  };

  const handleDeleteConnection = (connIndex) => {
    saveUndo();
    setConnections(prev => prev.filter((_, i) => i !== connIndex));
    setSelectedConnection(null);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedConnection !== null && (e.key === "Delete" || e.key === "Backspace")) {
        if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
        e.preventDefault();
        handleDeleteConnection(selectedConnection);
      }
      if (e.key === "Escape") {
        setSelectedConnection(null);
        setReconnectDrag(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedConnection, connections]);

  const handleDeleteNode = (nodeId) => {
    saveUndo();
    setNodes(prev => prev.filter(n => n.id !== nodeId));
    setConnections(prev => prev.filter(c => c.from !== nodeId && c.to !== nodeId));
    setCustomNodeLabels(prev => {
      if (!(nodeId in prev)) return prev;
      const next = { ...prev };
      delete next[nodeId];
      return next;
    });
    if (selectedNode === nodeId) setSelectedNode(null);
  };

  // C23 — Export / Import / Publish handlers.
  const handleExportTree = async () => {
    if (!currentAgentId) {
      setSaveResult({ success: false, error: "Save the agent first to export." });
      return;
    }
    try {
      const res = await agentApi.exportTree(currentAgentId);
      const snapshot = res?.data || res;
      const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(agentName || "agent").replace(/\s+/g, "_")}-tree.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSaveResult({ success: true, message: "Tree exported." });
      setTimeout(() => setSaveResult(null), 3000);
    } catch (e) {
      setSaveResult({ success: false, error: e.message || "Export failed" });
    }
  };

  const handleImportTree = async () => {
    setImportBusy(true); setImportErr(null);
    try {
      let payload;
      try { payload = JSON.parse(importJsonText); } catch (parseErr) {
        setImportErr("Invalid JSON: " + parseErr.message);
        return;
      }
      const res = await agentApi.importTree(payload);
      const data = res?.data || {};
      setShowImportModal(false);
      setImportJsonText("");
      setSaveResult({ success: true, message: `Imported (supervisor=${(data.supervisor_id || "").slice(0,8)}…, ${(data.expert_ids || []).length} experts)` });
      setTimeout(() => setSaveResult(null), 5000);
    } catch (e) {
      const detail = e?.body?.detail;
      let msg = e.message || "Import failed";
      if (typeof detail === "object") {
        if (detail.error === "missing_mcp_slug" || detail.error === "missing_openapi_slug") {
          msg = `Missing integration slug: ${detail.slug || "?"}. Register it in Admin first.`;
        } else if (detail.error === "credential_field_in_payload") {
          msg = "Payload contains a credential field. Strip it and re-import.";
        } else if (detail.error === "prompt_injection_detected" || detail.error === "prompt_injection_detected_in_expert") {
          msg = "Prompt-injection sentinel detected in payload. Sanitize and retry.";
        } else if (detail.error) {
          msg = `${detail.error}: ${detail.detail || ""}`;
        }
      }
      setImportErr(msg);
    } finally {
      setImportBusy(false);
    }
  };

  const handlePublishTree = async () => {
    if (!currentAgentId) {
      setPublishErr("Save the agent first to publish.");
      return;
    }
    if (!publishName.trim()) {
      setPublishErr("Name is required.");
      return;
    }
    setPublishBusy(true); setPublishErr(null);
    try {
      const res = await agentApi.publishTree({
        agent_id: currentAgentId,
        name: publishName.trim(),
        description: publishDescription.trim(),
        category: publishCategory,
        visibility: publishVisibility,
      });
      const data = res?.data || {};
      setShowPublishModal(false);
      setPublishName(""); setPublishDescription("");
      setSaveResult({ success: true, message: `Published "${data.name}" (${data.visibility})` });
      setTimeout(() => setSaveResult(null), 5000);
    } catch (e) {
      const detail = e?.body?.detail;
      let msg = e.message || "Publish failed";
      if (typeof detail === "object" && detail.error === "forbidden") {
        msg = detail.detail || "Forbidden — admin role required for visibility='global'.";
      }
      setPublishErr(msg);
    } finally {
      setPublishBusy(false);
    }
  };

  // C10 — Inline node renaming. The label change flows into:
  //   1. The canvas node's `label` for immediate visual feedback.
  //   2. customNodeLabels state, which the save flow writes into
  //      flow_config.canvas.node_labels[node_id] so the Detail Viewer's
  //      GraphTab (B8) can render the same custom label for that node.
  const handleNodeRename = (nodeId, newLabel) => {
    saveUndo();
    setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, label: newLabel } : n));
    setCustomNodeLabels(prev => ({ ...prev, [nodeId]: newLabel }));
  };

  const selectedNodeData = nodes.find(n => n.id === selectedNode);

  // Guided chat mode
  if (builderMode === "guided") {
    return (
      <div style={{ display: "flex", flex: 1, overflow: "hidden", position: "relative" }}>
        <AgentChatView
          availableModels={availableModels}
          onCreated={(agent) => {
            // After creation, switch to canvas and load the new agent
            if (agent) {
              setBuilderMode("canvas");
              // Trigger a reload of the agent list via parent
            }
          }}
          onSwitchToCanvas={() => setBuilderMode("canvas")}
        />
      </div>
    );
  }

  // 2026-05-24 deep-link UX: show a loading overlay while we fetch
  // the URL-specified agent, or a clear error banner if the fetch
  // failed (404, no access). Without these the user sees a blank
  // canvas and can't tell whether they're early or broken.
  if (deepLinkAgentId && !pendingEditAgent && deepLinkLoading) {
    return (
      <div data-builder-root style={{
        display: "flex", flex: 1, alignItems: "center", justifyContent: "center",
        flexDirection: "column", gap: 16, color: COLORS.textMuted,
      }}>
        <div style={{
          width: 32, height: 32, border: `3px solid ${COLORS.border}`,
          borderTopColor: COLORS.accentText, borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }}></div>
        <div>Loading agent…</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  if (deepLinkAgentId && !pendingEditAgent && deepLinkError) {
    return (
      <div data-builder-root style={{
        display: "flex", flex: 1, alignItems: "center", justifyContent: "center",
        flexDirection: "column", gap: 16, padding: 32,
      }}>
        <div style={{
          maxWidth: 480, padding: 24, borderRadius: RADIUS.lg,
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
          textAlign: "center",
        }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: COLORS.text }}>
            Couldn't load agent
          </div>
          <div style={{ fontSize: 14, color: COLORS.textMuted, marginBottom: 16 }}>
            {deepLinkError}
          </div>
          <Button onClick={() => navigate("/library")} variant="primary">
            Back to Agents
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-builder-root style={{ display: "flex", flex: 1, overflow: "hidden", position: "relative" }}>
      {/* C16-C18 — inline expert authoring wizard. Opens on Sub-Agent drop;
          on Save runs the §7.1 atomic /tree insert (C2) and patches the
          dropped canvas node with the new expert's id + name. */}
      <ExpertWizard
        open={wizardOpen}
        onClose={() => { setWizardOpen(false); setWizardForNodeId(null); }}
        parentSupervisor={currentAgentId ? { id: currentAgentId, name: agentName } : null}
        parentChannels={Object.entries(triggerChannels).filter(([, v]) => v).map(([k]) => k)}
        currentUser={currentUser}
        onCreated={(expert) => {
          // §19.6 — wizard now writes canvas state, not DB. Patch the
          // dropped canvas node with the full authored spec; the next
          // Save click collects it via collectUnsavedExpertPayloads
          // and apply_tree creates the row.
          //
          // Backward compat: if the wizard ever returns a real id (e.g.
          // a future direct-write mode), we still honor it.
          if (!wizardForNodeId) return;
          setNodes(prev => prev.map(n => {
            if (n.id !== wizardForNodeId) return n;
            return {
              ...n,
              label: expert.name,
              agentConfig: {
                ...(n.agentConfig || {}),
                agentId: expert.id || null,    // null = INSERT on next Save
                agentName: expert.name,
                agentStatus: expert._canvasOnly ? "draft" : "live",
                description: expert.description,
                systemPrompt: expert.system_prompt,
                channels: expert.channels,
                toolsets: expert.toolsets,
                domainGrammar: expert.domain_grammar,
                authoredVia: expert.authoredVia,
              },
            };
          }));
        }}
      />

      {/* Phase B11.1 — Shared AgentBuilderChat (same component as Agent
          Library "Create with AI"). Cmd+K toggles open/close. The chat
          surfaces the agent currently being edited so the Super Agent
          has context. */}
      {aiAssistOpen && (
        <AgentBuilderChat
          agent={currentAgentId
            ? { id: currentAgentId, name: agentName, provider: llmProvider, model: llmModel, status: "draft", channels: Object.entries(triggerChannels).filter(([, v]) => v).map(([k]) => k) }
            : { id: null, name: agentName || "New Agent", provider: "", model: "", status: "new", channels: [] }}
          agents={availableAgents}
          onClose={() => setAiAssistOpen(false)}
        />
      )}

      {/* AI Assist toggle — visible button at bottom-right of canvas
          (parity with the previous AIAssistPanel "✨ AI assist" pill).
          Cmd+K still toggles via the keydown listener above. Hidden
          while the panel is open since the panel has its own ✕ close. */}
      {!aiAssistOpen && (
        <button
          onClick={() => setAiAssistOpen(true)}
          title="Open AI assist (Cmd+K / Ctrl+K)"
          style={{
            position: "fixed", right: 16, bottom: bp.isDesktop ? 16 : 88,
            zIndex: 200,
            padding: "10px 14px", borderRadius: 999,
            background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`,
            color: "#fff", border: "none",
            boxShadow: "0 6px 20px rgba(0,0,0,0.45)",
            cursor: "pointer", fontSize: 12, fontWeight: 700,
            display: "flex", alignItems: "center", gap: 8,
          }}
        >
          <span style={{ fontSize: 14 }}>{"\u2728"}</span>
          <span>AI assist</span>
          <span style={{
            fontSize: 10, opacity: 0.85, padding: "1px 5px",
            background: "rgba(0,0,0,0.2)", borderRadius: 4, fontFamily: "monospace",
          }}>{"\u2318K"}</span>
        </button>
      )}

      {/* Floating "show components" toggle. Only renders when the
          palette is HIDDEN \u2014 when the palette is open, the panel's own
          \u2715 close button (inside its header) is the visible affordance.
          Avoids occluding the "Components" header label that was the
          BUG-024 user report. */}
      {!showPalette && (
        <button onClick={() => {
          setShowPalette(true);
          setShowConfig(false);
          try { localStorage.setItem("builder.palette.open", "1"); } catch {}
        }} title="Show components" style={{
          position: "absolute", top: 12, left: 12, zIndex: 200, width: 40, height: 40, borderRadius: 10,
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
          color: COLORS.textMuted, fontSize: 16, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
        }}>{"\u2B21"}</button>
      )}
      {!bp.isDesktop && (
        <button onClick={() => { setShowConfig(!showConfig); setShowPalette(false); }} style={{
          position: "absolute", top: 12, right: 12, zIndex: 200, width: 40, height: 40, borderRadius: 10,
          background: showConfig ? COLORS.accent : COLORS.surface, border: `1px solid ${COLORS.border}`,
          color: showConfig ? "#fff" : COLORS.textMuted, fontSize: 16, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
        }}>{"\u2699"}</button>
      )}

      {/* Left Palette — thickGlass per R3.4. Canvas itself stays solid
          (per §7.1 / decision #16: spring transforms + backdrop-filter
          compose poorly on canvas nodes). */}
      {showPalette && (
        <div className="panel-scroll" style={{
          width: bp.isDesktop ? 220 : 260,
          ...MATERIALS.thickGlass(),
          borderRadius: 0,
          borderRight: `1px solid ${COLORS.border}`,
          borderTop: "none", borderLeft: "none", borderBottom: "none",
          padding: "20px 14px", overflowY: "auto",
          ...(bp.isDesktop ? {} : {
            position: "absolute", top: 0, left: 0, bottom: bp.isMobile ? 60 : 0, zIndex: 150,
            boxShadow: "8px 0 32px rgba(0,0,0,0.5)", animation: "fadeIn 0.15s ease-out",
          }),
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>Components</span>
            <button onClick={() => {
              setShowPalette(false);
              try { localStorage.setItem("builder.palette.open", "0"); } catch {}
            }} title="Hide components" style={{
                width: 28, height: 28, borderRadius: 7, border: `1px solid ${COLORS.border}`,
                background: "transparent", color: COLORS.textMuted, fontSize: 14, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>{"\u2715"}</button>
            </div>
          {/* C4 — palette grouped into 7 sections per agent-builder-v1.md §5.3.
              Integrations + Workflows sections render dynamic items from
              tenant-registered rows; empty states show a "+ Register" CTA.
              Section headers toggle collapse. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {PALETTE_SECTIONS.map(section => {
              const collapsed = !!collapsedSections[section.id];
              const staticItems = (section.types || [])
                .map(t => PALETTE_ITEMS.find(p => p.type === t))
                .filter(Boolean);
              let dynamicItems = [];
              if (section.dynamic === "integrations") {
                const mcpIcon = "\u{1F50C}";
                const oapIcon = "\u{1F517}";
                dynamicItems = [
                  ...mcpServers.map(s => ({
                    type: "mcp_integration",
                    label: s.name || s.server_id || "MCP",
                    icon: mcpIcon, color: COLORS.accentText,
                    desc: `MCP: ${s.server_id || s.name}`,
                    bound_id: s.id, bound_kind: "mcp",
                  })),
                  ...openapiIntegrations.map(o => ({
                    type: "openapi_integration",
                    label: o.name || o.slug || "OpenAPI",
                    icon: oapIcon, color: COLORS.cyan,
                    desc: `OpenAPI: ${o.slug || o.name}`,
                    bound_id: o.id, bound_kind: "openapi",
                  })),
                ];
              }
              // WS4: the `workflows` dynamic palette section was retired, so
              // no section carries `dynamic === "workflows"` any more and the
              // branch that mapped `tenantWorkflows` into draggable nodes is
              // gone. Multi-step pipelines are authored via a supervisor's mode.
              return (
                <div key={section.id} style={{ marginBottom: 6 }}>
                  <button
                    onClick={() => setCollapsedSections(prev => ({ ...prev, [section.id]: !prev[section.id] }))}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      width: "100%", background: "transparent", border: "none",
                      padding: "6px 4px", cursor: "pointer",
                      fontSize: 10, fontWeight: 700, color: COLORS.textMuted,
                      letterSpacing: "0.08em", textTransform: "uppercase",
                    }}
                  >
                    <span>{section.label}</span>
                    <span style={{ fontSize: 9 }}>{collapsed ? "\u25B8" : "\u25BE"}</span>
                  </button>
                  {!collapsed && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 4 }}>
                      {/* Static items */}
                      {staticItems.map(item => (
                        <PaletteItem key={item.type} item={item} draggedItem={draggedItem} setDraggedItem={setDraggedItem} />
                      ))}
                      {/* Dynamic items */}
                      {dynamicItems.map(item => (
                        <PaletteItem key={`${item.type}:${item.bound_id}`} item={item} draggedItem={draggedItem} setDraggedItem={setDraggedItem} />
                      ))}
                      {section.dynamic && dynamicItems.length === 0 && (
                        <div style={{
                          fontSize: 11, color: COLORS.textDim, padding: "6px 10px",
                          border: `1px dashed ${COLORS.border}`, borderRadius: 8,
                        }}>
                          {section.dynamic === "integrations"
                            ? "No integrations registered. Register MCP or OpenAPI in Admin → Integrations."
                            : "No workflows. Create one in Admin → Workflows."}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        <div style={{ marginTop: 24, fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>
          Orchestration Templates
        </div>
        {ORCHESTRATION_TEMPLATES.map(t => (
          <div key={t.id} title={t.desc} onClick={() => {
            saveUndo();
            setNodes(t.nodes.map(n => ({ ...n, agentConfig: n.agentConfig ? { ...n.agentConfig } : undefined })));
            setConnections(t.connections.map(c => ({ ...c })));
            setNodeCounter(t.nodes.length + 1);
            setSelectedNode(null);
          }} style={{
            padding: "10px 12px", borderRadius: 10, marginBottom: 4, cursor: "pointer",
            fontSize: 12, color: COLORS.textMuted, transition: "all 0.15s",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = COLORS.surfaceHover; e.currentTarget.style.color = COLORS.text; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = COLORS.textMuted; }}
          >
            <span style={{ marginRight: 6 }}>{t.icon}</span>{t.label}
            <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>{t.desc}</div>
          </div>
        ))}
      </div>
      )}

      {/* Canvas */}
      <div ref={canvasRef}
        className="canvas-scroll"
        style={{
          flex: 1, position: "relative", overflow: "auto",
          cursor: panState.current.panning ? "grabbing"
                : (connectingFrom || reconnectDrag) ? "crosshair"
                : "default",
        }}
        onDragOver={e => { e.preventDefault(); setCanvasDragOver(true); }}
        onDragLeave={() => setCanvasDragOver(false)}
        onDrop={handleCanvasDrop}
        onMouseDown={handleCanvasMouseDown}
        onMouseMove={handleCanvasMouseMove}
        onMouseUp={handleCanvasMouseUp}
        onClick={() => {
          if (panState.current.justPanned) { panState.current.justPanned = false; return; }
          setSelectedNode(null); setSelectedConnection(null); setReconnectDrag(null);
        }}
      >
        {/* Large scrollable inner area */}
        <div style={{
          position: "relative",
          width: Math.max(1800, ...nodes.map(n => n.x + 200)) * (zoom / 100),
          height: Math.max(1200, ...nodes.map(n => n.y + 120)) * (zoom / 100),
          minWidth: "100%", minHeight: "100%",
        }}>
          {/* Grid background */}
          <div style={{
            position: "absolute", inset: 0, opacity: 0.07,
            backgroundImage: `radial-gradient(${COLORS.textMuted} 1px, transparent 1px)`,
            backgroundSize: `${24 * zoom / 100}px ${24 * zoom / 100}px`,
          }} />

          {/* Agent pipeline label */}
          {currentAgentId && (
            <div style={{
              position: "absolute", top: 18, left: 0, right: 0, zIndex: 10,
              display: "flex", justifyContent: "center", pointerEvents: "none",
            }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 10, padding: "8px 20px",
                background: `${COLORS.accent}12`, border: `1px solid ${COLORS.accent}30`,
                borderRadius: 10, backdropFilter: "blur(6px)",
              }}>
                <div style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: COLORS.accent, boxShadow: `0 0 8px ${COLORS.accent}60`,
                }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.accentText, letterSpacing: "0.01em" }}>
                  {agentName}
                </span>
                <span style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 500 }}>
                  {agentVersion} pipeline
                </span>
              </div>
            </div>
          )}

          {/* Drop zone overlay */}
          {canvasDragOver && (
            <div style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, pointerEvents: "none",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <div style={{
                padding: "10px 24px", borderRadius: 10,
                background: `${COLORS.surface}ee`, border: `1px solid ${COLORS.accent}30`,
                fontSize: 13, color: COLORS.accentText, fontWeight: 600,
                boxShadow: `0 4px 20px rgba(0,0,0,0.3)`,
              }}>Drop to add node</div>
            </div>
          )}

          {/* Scaled canvas content */}
          <div style={{
            position: "absolute", top: 0, left: 0,
            transform: `scale(${zoom / 100})`, transformOrigin: "0 0",
            width: Math.max(1800, ...nodes.map(n => n.x + 200)),
            height: Math.max(1200, ...nodes.map(n => n.y + 120)),
          }}>
            <CanvasConnections nodes={nodes} connections={connections} mousePos={mousePos} connectingFrom={connectingFrom}
              selectedConnection={selectedConnection}
              onSelectConnection={(i) => { setSelectedConnection(i); setSelectedNode(null); }}
              reconnectDrag={reconnectDrag}
              onReconnectStart={handleReconnectStart}
            />
            {nodes.map(node => (
              <DraggableNode key={node.id} node={node}
                isSelected={selectedNode === node.id}
                onClick={(id) => { setSelectedNode(id); setSelectedConnection(null); }}
                onDragStart={() => saveUndo()}
                onDrag={handleNodeDrag}
                onDragEnd={() => {}}
                onPortMouseDown={handlePortMouseDown}
                onPortMouseUp={handlePortMouseUp}
                onLabelChange={handleNodeRename}
                onDelete={handleDeleteNode}
                connectingFrom={connectingFrom}
              />
            ))}
          </div>
        </div>

        {/* Unified canvas toolbar (bottom) — floating + draggable by the
            handle on its left edge. position:fixed so it stays put when
            the canvas scrolls. Default centered along the viewport
            bottom; drag-position persists in localStorage. */}
        <div style={{
          position: "fixed",
          ...(toolbarPos.x !== null && toolbarPos.y !== null
            ? { left: toolbarPos.x, top: toolbarPos.y, bottom: "auto", transform: "none" }
            : { bottom: 20, left: "50%", transform: "translateX(-50%)" }),
          display: "flex", alignItems: "center", gap: 2, background: `${COLORS.surface}ee`, borderRadius: 12,
          border: `1px solid ${COLORS.border}`, padding: 4, boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          backdropFilter: "blur(8px)", width: "fit-content",
          zIndex: 50,
        }}>
          {/* Drag handle — grab here to reposition the whole toolbar. */}
          <div
            onMouseDown={handleToolbarDragStart}
            title="Drag to move toolbar"
            style={{
              width: 16, height: 36, display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "grab", color: COLORS.textMuted, fontSize: 12, userSelect: "none",
              borderRight: `1px solid ${COLORS.border}`, marginRight: 4,
            }}
          >{"⠇"}</div>
          {toolbarPos.x !== null && (
            <button
              onClick={() => setToolbarPos({ x: null, y: null })}
              title="Reset toolbar to default position"
              style={{
                width: 20, height: 20, borderRadius: 4, border: `1px solid ${COLORS.border}`,
                background: "transparent", color: COLORS.textMuted, fontSize: 10, cursor: "pointer",
                alignSelf: "center", marginRight: 4,
              }}
            >{"↺"}</button>
          )}
          {/* Agent name + version */}
          <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "0 8px" }}>
            <input
              type="text" value={agentName} onChange={e => setAgentName(e.target.value)}
              placeholder="Agent name"
              style={{
                background: "transparent", border: "none", outline: "none", color: COLORS.text,
                fontSize: 12, fontWeight: 600, width: 120, padding: 0,
              }}
            />
            <input
              type="text" value={agentVersion} onChange={e => setAgentVersion(e.target.value)}
              placeholder="v1.0"
              style={{
                background: `${COLORS.accent}10`, border: `1px solid ${COLORS.accent}25`, borderRadius: 5,
                outline: "none", color: COLORS.accentText, fontSize: 9, fontWeight: 600,
                width: 34, padding: "2px 4px", textAlign: "center",
              }}
            />
          </div>
          <div style={{ width: 1, height: 20, background: COLORS.border, alignSelf: "center" }} />
          <ToolbarBtn iconWidth={36} onClick={() => setZoom(z => Math.max(10, z - 10))}>{"\u2212"}</ToolbarBtn>
          <ToolbarBtn iconWidth={52} onClick={() => setZoom(100)} title="Click to reset. Drag empty canvas to pan. Ctrl/Cmd+wheel (or trackpad pinch) to zoom.">{zoom}%</ToolbarBtn>
          <ToolbarBtn iconWidth={36} onClick={() => setZoom(z => Math.min(200, z + 10))}>+</ToolbarBtn>
          <ToolbarBtn iconWidth={36} title="Fit canvas to view" onClick={() => fitToView()}>{"\u26f6"}</ToolbarBtn>
          <div style={{ width: 1, height: 20, background: COLORS.border, alignSelf: "center" }} />
          <ToolbarBtn iconWidth={36} title="Undo" onClick={handleUndo}
            disabled={undoStack.length === 0}
            color={undoStack.length > 0 ? COLORS.accent : undefined}>{"\u21BA"}</ToolbarBtn>
          <ToolbarBtn iconWidth={36} title="Delete selected"
            disabled={!selectedNode}
            color={selectedNode ? COLORS.pink : undefined}
            onClick={() => { if (selectedNode) handleDeleteNode(selectedNode); }}>{"\u2715"}</ToolbarBtn>
          <div style={{ width: 1, height: 20, background: COLORS.border, alignSelf: "center" }} />
          <ToolbarBtn title="Save agent" disabled={saving} color={COLORS.green}
            onClick={handleSaveAgent}>{saving ? "..." : "Save"}</ToolbarBtn>
          <ToolbarBtn title="Save as new agent" color={COLORS.cyan}
            onClick={() => { setSaveAsName(agentName === "Untitled Agent" ? "" : agentName); setSaveAsVersion(agentVersion); setShowSaveAsModal(true); }}>
            Save As</ToolbarBtn>
          <ToolbarBtn title="Load agent" color={COLORS.accent}
            onClick={handleLoadAgentsList}>Load</ToolbarBtn>
          {/* C23 — Export / Import / Publish */}
          <div style={{ width: 1, height: 20, background: COLORS.border, alignSelf: "center" }} />
          <ToolbarBtn title="Export tree as JSON" disabled={!currentAgentId}
            onClick={handleExportTree}>{"\u2B73"} Export</ToolbarBtn>
          <ToolbarBtn title="Import tree from JSON"
            onClick={() => { setImportErr(null); setImportJsonText(""); setShowImportModal(true); }}>
            {"\u2B71"} Import</ToolbarBtn>
          <ToolbarBtn title="Publish to Marketplace" disabled={!currentAgentId}
            color={COLORS.purple} active={!!currentAgentId}
            onClick={() => {
              setPublishErr(null);
              setPublishName(agentName === "Untitled Agent" ? "" : agentName);
              setPublishDescription("");
              setShowPublishModal(true);
            }}>{"\u2734"} Publish</ToolbarBtn>
          <div style={{ width: 1, height: 20, background: COLORS.border, alignSelf: "center" }} />
          <ToolbarBtn title="Switch to guided chat builder"
            color={COLORS.green} active
            onClick={() => setBuilderMode("guided")}>{"\uD83D\uDCAC"} Guided</ToolbarBtn>
          <div style={{ width: 1, height: 20, background: COLORS.border, alignSelf: "center" }} />
          <span style={{ fontSize: 10, color: COLORS.textDim, whiteSpace: "nowrap", padding: "0 4px" }}>
            {nodes.length} nodes {"\u00B7"} {connections.length} edges
          </span>
          {currentAgentId && (
            <>
              <div style={{ width: 1, height: 20, background: COLORS.border, alignSelf: "center" }} />
              <button onClick={() => {
                setCurrentAgentId(null); setAgentName("Untitled Agent"); setAgentVersion("v1.0");
                treeVersionRef.current = 0; loadedExpertIdsRef.current = new Set(); loadedWorkflowIdsRef.current = new Set();  // D3/D9: fresh canvas baseline
                setNodes(INITIAL_NODES); setConnections(INITIAL_CONNECTIONS); setZoom(100);
                setNodeCounter(2); setSelectedNode("llm-1");
                setTemperature(0.7); setSystemPrompt("You are a friendly and professional customer support agent for our company. Always greet the customer warmly, answer questions using only the provided knowledge base, never fabricate information, and escalate to a human agent if you cannot resolve the issue within 3 turns.");
                setTriggerChannels({ whatsapp: true, voice: true, chat: true, sms: false, email: false, webhook: false }); setTriggerName("");
              }} style={{
                background: "transparent", border: "none", color: COLORS.accentText,
                fontSize: 10, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", padding: "0 6px",
              }}>+ New</button>
            </>
          )}
        </div>

        {saveResult && (
          <div style={{
            position: "sticky", bottom: 60, left: "50%", transform: "translateX(-50%)",
            padding: "8px 16px", borderRadius: 8, fontSize: 11, fontWeight: 500,
            background: saveResult.success ? `${COLORS.green}18` : `${COLORS.pink}18`,
            border: `1px solid ${saveResult.success ? COLORS.green : COLORS.pink}30`,
            color: saveResult.success ? COLORS.green : COLORS.pink,
            width: "fit-content", margin: "0 auto", zIndex: 20,
            boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
          }}>
            {saveResult.success ? saveResult.message : `Error: ${saveResult.error}`}
          </div>
        )}
      </div>

      {/* Right Panel - Node config (hidden when no node selected) */}
      {selectedNodeData && (bp.isDesktop || showConfig) && (
      <div className="panel-scroll" style={{
        width: bp.isDesktop ? 280 : bp.isTablet ? 300 : "100%",
        ...MATERIALS.thickGlass(),
        borderRadius: 0,
        borderLeft: bp.isDesktop ? `1px solid ${COLORS.border}` : "none",
        borderTop: "none", borderRight: "none", borderBottom: "none",
        padding: "20px 16px", overflowY: "auto",
        ...(bp.isDesktop ? {} : {
          position: "absolute", top: 0, right: 0, bottom: bp.isMobile ? 60 : 0, zIndex: 150,
          boxShadow: "-8px 0 32px rgba(0,0,0,0.5)", animation: "fadeIn 0.15s ease-out",
          borderLeft: `1px solid ${COLORS.border}`,
        }),
      }}>
        {!bp.isDesktop && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>Node Config</span>
            <button onClick={() => setShowConfig(false)} style={{
              width: 28, height: 28, borderRadius: 7, border: `1px solid ${COLORS.border}`,
              background: "transparent", color: COLORS.textMuted, fontSize: 14, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>{"\u2715"}</button>
          </div>
        )}
        <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>Node Config</div>
              <Badge color={selectedNodeData.color}>{selectedNodeData.label}</Badge>
            </div>

            {/* Node ID */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Node ID</div>
              <div style={{
                width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                borderRadius: RADIUS.deep, padding: "8px 10px", color: COLORS.textDim, fontSize: 11, fontFamily: "monospace",
              }}>{selectedNodeData.id}</div>
            </div>

            {/* LLM-specific config */}
            {selectedNodeData.type === "llm" && (
              <>
                {/* LLM Provider & Model */}
                <div style={{
                  marginBottom: 16, padding: "12px", borderRadius: RADIUS.deep,
                  background: `${COLORS.accent}06`, border: `1px solid ${COLORS.accent}18`,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.accentText, marginBottom: 10, letterSpacing: "0.04em", textTransform: "uppercase" }}>LLM Configuration</div>

                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4, fontWeight: 500 }}>Provider</div>
                    <select
                      value={llmProvider}
                      onChange={e => {
                        const p = e.target.value;
                        setLlmProvider(p);
                        const models = availableModels[p] || [];
                        setLlmModel(models.length ? models[0].id : "");
                      }}
                      style={{
                        width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                        borderRadius: RADIUS.deep, padding: "8px 10px", color: COLORS.text, fontSize: 12, outline: "none", cursor: "pointer",
                      }}
                    >
                      <option value="openai">OpenAI</option>
                      <option value="anthropic">Anthropic</option>
                      <option value="vertex">Google Vertex AI</option>
                      <option value="bedrock">AWS Bedrock</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4, fontWeight: 500 }}>Model</div>
                    <select
                      value={llmModel}
                      onChange={e => setLlmModel(e.target.value)}
                      style={{
                        width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                        borderRadius: RADIUS.deep, padding: "8px 10px", color: COLORS.text, fontSize: 12, outline: "none", cursor: "pointer",
                      }}
                    >
                      <option value="">— select model —</option>
                      {(availableModels[llmProvider] || []).filter(m => m.kind === "chat").map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.green }} />
                    <span style={{ fontSize: 10, color: COLORS.textDim }}>API keys managed via backend configuration</span>
                  </div>

                  {/* Ranked Fallback Toggle */}
                  <div style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 500 }}>Ranked Fallback</span>
                    <button
                      data-testid="builder-llm-ranked-fallback-toggle"
                      onClick={() => { setLlmRankingsMode(!llmRankingsMode); if (!llmRankingsMode && llmRankings.length === 0) setLlmRankings([{ provider: llmProvider, model: llmModel }]); }}
                      style={{
                        padding: "3px 10px", fontSize: 10, borderRadius: 6, cursor: "pointer",
                        background: llmRankingsMode ? COLORS.accent : "transparent",
                        color: llmRankingsMode ? "#fff" : COLORS.textDim,
                        border: `1px solid ${llmRankingsMode ? COLORS.accent : COLORS.border}`,
                      }}>
                      {llmRankingsMode ? "ON" : "OFF"}
                    </button>
                  </div>

                  {llmRankingsMode && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 6 }}>
                        If the first provider fails, the system tries the next in order.
                      </div>
                      {llmRankings.map((entry, idx) => (
                        <div key={idx} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.accentText, width: 14, textAlign: "center" }}>{idx + 1}</span>
                          <select value={entry.provider} onChange={e => {
                            const p = e.target.value;
                            const models = availableModels[p] || [];
                            const defModel = models.length ? models[0].id : "";
                            setLlmRankings(prev => prev.map((r, i) => i === idx ? { provider: p, model: defModel } : r));
                          }} style={{ flex: 1, padding: "4px 6px", fontSize: 11, borderRadius: 6, background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text, outline: "none", cursor: "pointer", colorScheme: "dark" }}>
                            <option value="openai" style={{ background: COLORS.surface, color: COLORS.text }}>OpenAI</option>
                            <option value="anthropic" style={{ background: COLORS.surface, color: COLORS.text }}>Anthropic</option>
                            <option value="vertex" style={{ background: COLORS.surface, color: COLORS.text }}>Vertex AI</option>
                            <option value="bedrock" style={{ background: COLORS.surface, color: COLORS.text }}>AWS Bedrock</option>
                          </select>
                          <select value={entry.model} onChange={e => setLlmRankings(prev => prev.map((r, i) => i === idx ? { ...r, model: e.target.value } : r))}
                            style={{ flex: 2, padding: "4px 6px", fontSize: 11, borderRadius: 6, background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text, outline: "none", cursor: "pointer", colorScheme: "dark" }}>
                            {(availableModels[entry.provider] || []).map(m => (
                              <option key={m.id} value={m.id} style={{ background: COLORS.surface, color: COLORS.text }}>{m.name}</option>
                            ))}
                          </select>
                          <button onClick={() => setLlmRankings(prev => { const n = [...prev]; const j = idx - 1; if (j >= 0) [n[idx], n[j]] = [n[j], n[idx]]; return n; })}
                            disabled={idx === 0} style={{ background: "none", border: "none", cursor: idx === 0 ? "default" : "pointer", color: idx === 0 ? COLORS.border : COLORS.textMuted, fontSize: 12, padding: 0 }}>&#9650;</button>
                          <button onClick={() => setLlmRankings(prev => { const n = [...prev]; const j = idx + 1; if (j < n.length) [n[idx], n[j]] = [n[j], n[idx]]; return n; })}
                            disabled={idx === llmRankings.length - 1} style={{ background: "none", border: "none", cursor: idx === llmRankings.length - 1 ? "default" : "pointer", color: idx === llmRankings.length - 1 ? COLORS.border : COLORS.textMuted, fontSize: 12, padding: 0 }}>&#9660;</button>
                          <button onClick={() => setLlmRankings(prev => prev.filter((_, i) => i !== idx))}
                            style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.pink, fontSize: 12, padding: 0 }}>&times;</button>
                        </div>
                      ))}
                      {llmRankings.length < 5 && (
                        <button onClick={() => {
                          // BYOK-strict: pick first available provider/model from live fetch; blank if none configured.
                          const firstProv = Object.keys(availableModels).find(p => (availableModels[p] || []).length) || "";
                          const firstModel = firstProv ? (availableModels[firstProv][0]?.id || "") : "";
                          setLlmRankings(prev => [...prev, { provider: firstProv, model: firstModel }]);
                        }}
                          style={{ fontSize: 10, color: COLORS.accentText, background: "none", border: `1px dashed ${COLORS.border}`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", width: "100%", marginTop: 2 }}>
                          + Add Provider
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Max Tokens</div>
                  <input
                    type="number"
                    value={llmMaxTokens}
                    onChange={e => setLlmMaxTokens(Number(e.target.value))}
                    min={1} max={128000}
                    style={{
                      width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                      borderRadius: RADIUS.deep, padding: "8px 10px", color: COLORS.text, fontSize: 12, outline: "none",
                    }}
                  />
                </div>

                {/* Temperature slider */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 500 }}>Temperature</div>
                    <div style={{
                      fontSize: 13, fontWeight: 700, color: COLORS.accentText,
                      background: `${COLORS.accent}12`, padding: "2px 8px", borderRadius: 6,
                      fontFamily: "'DM Sans', monospace",
                    }}>{temperature.toFixed(2)}</div>
                  </div>
                  <div style={{ position: "relative", height: 32, display: "flex", alignItems: "center" }}>
                    <input type="range" min="0" max="100" value={temperature * 100}
                      onChange={e => setTemperature(Number(e.target.value) / 100)}
                      style={{
                        width: "100%", height: 6, appearance: "none", WebkitAppearance: "none",
                        background: "transparent", cursor: "pointer", position: "relative", zIndex: 2,
                      }} />
                    <div style={{
                      position: "absolute", left: 0, right: 0, top: "50%", transform: "translateY(-50%)",
                      height: 6, borderRadius: 3, background: COLORS.surface, overflow: "hidden", pointerEvents: "none",
                      border: `1px solid ${COLORS.border}`,
                    }}>
                      <div style={{
                        height: "100%", width: `${temperature * 100}%`, borderRadius: 3,
                        background: `linear-gradient(90deg, ${COLORS.accent}, ${temperature > 0.7 ? COLORS.orange : COLORS.cyan})`,
                        boxShadow: `0 0 10px ${COLORS.accentGlow}`, transition: "width 0.05s, background 0.3s",
                      }} />
                    </div>
                    <style>{`
                      input[type="range"]::-webkit-slider-thumb {
                        -webkit-appearance: none; appearance: none;
                        width: 18px; height: 18px; border-radius: 50%;
                        background: ${COLORS.accent}; border: 3px solid ${COLORS.surface};
                        box-shadow: 0 0 10px ${COLORS.accentGlow}, 0 2px 6px rgba(0,0,0,0.4);
                        cursor: pointer; position: relative; z-index: 3;
                      }
                      input[type="range"]::-moz-range-thumb {
                        width: 18px; height: 18px; border-radius: 50%;
                        background: ${COLORS.accent}; border: 3px solid ${COLORS.surface};
                        box-shadow: 0 0 10px ${COLORS.accentGlow}, 0 2px 6px rgba(0,0,0,0.4); cursor: pointer;
                      }
                      input[type="range"]::-webkit-slider-runnable-track { background: transparent; height: 6px; }
                      input[type="range"]::-moz-range-track { background: transparent; height: 6px; }
                    `}</style>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                    <span style={{ fontSize: 9, color: COLORS.textDim, fontWeight: 500 }}>Precise</span>
                    <span style={{ fontSize: 9, color: COLORS.textDim, fontWeight: 500 }}>Balanced</span>
                    <span style={{ fontSize: 9, color: COLORS.textDim, fontWeight: 500 }}>Creative</span>
                  </div>
                  <div style={{
                    marginTop: 8, padding: "8px 10px", borderRadius: 8,
                    background: temperature < 0.3 ? `${COLORS.cyan}08` : temperature > 0.7 ? `${COLORS.orange}08` : `${COLORS.accent}08`,
                    border: `1px solid ${temperature < 0.3 ? COLORS.cyan + "18" : temperature > 0.7 ? COLORS.orange + "18" : COLORS.accent + "18"}`,
                    transition: "all 0.3s",
                  }}>
                    <div style={{
                      fontSize: 10, lineHeight: 1.5,
                      color: temperature < 0.3 ? COLORS.cyan : temperature > 0.7 ? COLORS.orange : COLORS.textMuted,
                      transition: "color 0.3s",
                    }}>
                      {temperature < 0.3 ? "\uD83C\uDFAF Highly focused \u2014 best for factual Q&A and deterministic tasks."
                        : temperature > 0.7 ? "\uD83C\uDFA8 Highly creative \u2014 best for brainstorming and diverse responses."
                        : "\u2696\uFE0F Balanced \u2014 good mix of coherence and variety."}
                    </div>
                  </div>
                </div>

                {/* System Prompt editor */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 500 }}>System Prompt</div>
                    <button onClick={() => setPromptExpanded(!promptExpanded)} style={{
                      fontSize: 9, padding: "2px 7px", borderRadius: 5, border: `1px solid ${COLORS.border}`,
                      background: "transparent", color: COLORS.textMuted, cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 3,
                    }}>{promptExpanded ? "\u25BE Collapse" : "\u25B8 Expand"}</button>
                  </div>
                  <div style={{
                    position: "relative", borderRadius: 10, overflow: "hidden",
                    border: `1px solid ${promptFocused ? COLORS.accent + "60" : COLORS.border}`,
                    boxShadow: promptFocused ? `0 0 12px ${COLORS.accentGlow}` : "none", transition: "all 0.2s",
                  }}>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 2, padding: "5px 8px",
                      background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`,
                    }}>
                      {[{ label: "B", tip: "Bold" }, { label: "\uD835\uDC3C", tip: "Italic" }, { label: "{ }", tip: "Variable" }].map(btn => (
                        <button key={btn.label} title={btn.tip} style={{
                          width: 26, height: 22, borderRadius: 4, border: "none",
                          background: "transparent", color: COLORS.textMuted, fontSize: 11, cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: btn.label === "B" ? 700 : 400,
                        }}>{btn.label}</button>
                      ))}
                      <div style={{ flex: 1 }} />
                      <span style={{ fontSize: 9, color: COLORS.textDim, fontFamily: "monospace" }}>{systemPrompt.length}/2000</span>
                    </div>
                    <textarea value={systemPrompt} onChange={e => setSystemPrompt(e.target.value)}
                      onFocus={() => setPromptFocused(true)} onBlur={() => setPromptFocused(false)}
                      style={{
                        width: "100%", boxSizing: "border-box", background: COLORS.surface, color: COLORS.text, fontSize: 12,
                        padding: "10px 12px", border: "none", outline: "none", resize: "none",
                        fontFamily: "'DM Sans', sans-serif", lineHeight: 1.65,
                        minHeight: promptExpanded ? 200 : 72, maxHeight: promptExpanded ? 400 : 72,
                        transition: "min-height 0.3s ease, max-height 0.3s ease",
                      }}
                      placeholder="Define your agent's personality, behavior, and rules..."
                    />
                    <div style={{
                      display: "flex", gap: 4, padding: "6px 8px", flexWrap: "wrap",
                      background: COLORS.surface, borderTop: `1px solid ${COLORS.border}`,
                    }}>
                      <span style={{ fontSize: 9, color: COLORS.textDim, marginRight: 4, lineHeight: "20px" }}>Insert:</span>
                      {[
                        { label: "{{user.name}}", color: COLORS.green },
                        { label: "{{channel}}", color: COLORS.orange },
                        { label: "{{context}}", color: COLORS.cyan },
                      ].map(v => (
                        <button key={v.label} onClick={() => setSystemPrompt(prev => prev + " " + v.label)}
                          style={{
                            fontSize: 9, padding: "2px 7px", borderRadius: 5, cursor: "pointer",
                            background: `${v.color}10`, border: `1px solid ${v.color}25`,
                            color: v.color, fontFamily: "monospace", fontWeight: 500,
                          }}>{v.label}</button>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={handleLlmTest} disabled={llmTesting} style={{
                    flex: 1, padding: "10px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`,
                    cursor: llmTesting ? "wait" : "pointer",
                    background: COLORS.surface, color: COLORS.textMuted,
                    fontSize: 12, fontWeight: 600, transition: "all 0.2s",
                  }}>{llmTesting ? "Testing..." : "Test Connection"}</button>
                </div>

                {llmTestResult && (
                  <div style={{
                    marginTop: 8, padding: "10px 12px", borderRadius: 8,
                    background: llmTestResult.success ? `${COLORS.green}10` : `${COLORS.pink}10`,
                    border: `1px solid ${llmTestResult.success ? COLORS.green : COLORS.pink}25`,
                    fontSize: 11, lineHeight: 1.5,
                    color: llmTestResult.success ? COLORS.green : COLORS.pink,
                  }}>
                    {llmTestResult.success
                      ? <>Connection Successful<br/><span style={{ color: COLORS.textMuted }}>Response: "{llmTestResult.reply}"</span></>
                      : <>Connection failed: {llmTestResult.error}</>
                    }
                  </div>
                )}
              </>
            )}

            {/* Trigger-specific config */}
            {selectedNodeData.type === "trigger" && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Trigger Name</div>
                  <input
                    type="text"
                    value={triggerName}
                    onChange={e => setTriggerName(e.target.value)}
                    placeholder="e.g. Support Trigger"
                    style={{
                      width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                      borderRadius: RADIUS.deep, padding: "8px 10px", color: COLORS.text, fontSize: 12, outline: "none",
                    }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Event Type</div>
                  <div style={{
                    width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                    borderRadius: RADIUS.deep, padding: "8px 10px", color: COLORS.text, fontSize: 12,
                  }}>Incoming Message</div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Source Channels</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {[
                      { key: "whatsapp", label: "WhatsApp", icon: "\uD83D\uDCAC" },
                      { key: "voice", label: "Voice", icon: "\uD83D\uDCDE" },
                      { key: "chat", label: "Chat", icon: "\uD83C\uDF10" },
                      { key: "sms", label: "SMS", icon: "\uD83D\uDCF1" },
                      { key: "email", label: "Email", icon: "\u2709" },
                      { key: "webhook", label: "Webhook", icon: "\uD83D\uDD17" },
                    ].map(ch => {
                      const active = triggerChannels[ch.key];
                      return (
                        <div key={ch.key} onClick={() => setTriggerChannels(prev => ({ ...prev, [ch.key]: !prev[ch.key] }))} style={{
                          flex: "1 1 18%", minWidth: 52, padding: "8px 4px", borderRadius: 8, textAlign: "center", cursor: "pointer",
                          background: active ? `${COLORS.green}12` : COLORS.bg,
                          border: `1px solid ${active ? COLORS.green + "40" : COLORS.border}`,
                          transition: "all 0.15s",
                        }}>
                          <div style={{ fontSize: 14 }}>{ch.icon}</div>
                          <div style={{ fontSize: 10, color: active ? COLORS.green : COLORS.textMuted, marginTop: 3, fontWeight: 500 }}>{ch.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Voice Config (shown when voice channel is enabled) */}
                {triggerChannels.voice && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 500 }}>Voice Config (overrides tenant defaults)</div>
                    {/* G-LINK — deep link to the unified Voice
                        Console so the builder can sanity-check
                        tenant-level voice settings + drift before
                        publishing an agent that depends on them. */}
                    <a
                      href="#admin-voice-console"
                      onClick={(e) => {
                        e.preventDefault();
                        window.dispatchEvent(new CustomEvent("admin:goto-tab", { detail: { id: "voice_console" } }));
                      }}
                      style={{ fontSize: 10, color: COLORS.textDim, textDecoration: "none" }}
                    >
                      Open Voice Console →
                    </a>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 10, color: COLORS.textDim }}>ElevenLabs Voice ID</label>
                      <input type="text" placeholder="Leave empty for tenant default" value={voiceConfig.voice_id}
                        onChange={e => setVoiceConfig(prev => ({ ...prev, voice_id: e.target.value }))}
                        style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 11 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: COLORS.textDim }}>Model</label>
                      <select value={voiceConfig.model} onChange={e => setVoiceConfig(prev => ({ ...prev, model: e.target.value }))}
                        style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 11 }}>
                        <option value="eleven_turbo_v2_5">Turbo v2.5</option>
                        <option value="eleven_flash_v2_5">Flash v2.5</option>
                        <option value="eleven_multilingual_v2">Multilingual v2</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: COLORS.textDim }}>Stability ({voiceConfig.stability})</label>
                      <input type="range" min="0" max="1" step="0.05" value={voiceConfig.stability}
                        onChange={e => setVoiceConfig(prev => ({ ...prev, stability: e.target.value }))}
                        style={{ width: "100%" }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: COLORS.textDim }}>Similarity ({voiceConfig.similarity_boost})</label>
                      <input type="range" min="0" max="1" step="0.05" value={voiceConfig.similarity_boost}
                        onChange={e => setVoiceConfig(prev => ({ ...prev, similarity_boost: e.target.value }))}
                        style={{ width: "100%" }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: COLORS.textDim }}>Style ({voiceConfig.style})</label>
                      <input type="range" min="0" max="1" step="0.05" value={voiceConfig.style}
                        onChange={e => setVoiceConfig(prev => ({ ...prev, style: e.target.value }))}
                        style={{ width: "100%" }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: COLORS.textDim }}>Speed ({voiceConfig.speed})</label>
                      <input type="range" min="0.7" max="1.3" step="0.05" value={voiceConfig.speed}
                        onChange={e => setVoiceConfig(prev => ({ ...prev, speed: e.target.value }))}
                        style={{ width: "100%" }} />
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 6 }}>Leave Voice ID empty to use the tenant default voice.</div>
                  {/* D4 — Per-agent outbound recording-disclosure toggle.
                      Default true (most jurisdictions are two-party-consent).
                      Toggle off only for one-party-consent regions where
                      the customer has already attested to consent on
                      the campaign-create form. */}
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 10, cursor: "pointer" }}>
                    <input type="checkbox"
                      checked={voiceConfig.outbound_disclosure_enabled !== false}
                      onChange={e => setVoiceConfig(prev => ({ ...prev, outbound_disclosure_enabled: e.target.checked }))} />
                    <span style={{ fontSize: 11, color: COLORS.text }}>
                      Auto-prepend "This call may be recorded" disclosure on outbound calls.
                      <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>
                        Recommended on. Required for two-party-consent jurisdictions (CA, FL, IL, MA, MD, MT, NH, PA, WA, etc.).
                      </div>
                    </span>
                  </label>
                </div>
                )}

                {/* Agent Routing Config */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Agent Routing</div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: "pointer" }}>
                    <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />
                    <span style={{ fontSize: 12, color: COLORS.text }}>Default / catch-all agent</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: "pointer" }}>
                    <input type="checkbox" checked={isDynamicAgent} onChange={e => setIsDynamicAgent(e.target.checked)} />
                    <span style={{ fontSize: 12, color: COLORS.text }}>Dynamic Agent</span>
                    <span style={{ fontSize: 10, color: COLORS.textDim }}>(can invoke other agents at runtime)</span>
                  </label>
                  {isDynamicAgent && (
                    <div style={{
                      padding: "8px 12px", borderRadius: RADIUS.deep, marginBottom: 10,
                      background: `${COLORS.accent}10`, border: `1px solid ${COLORS.accent}25`,
                      fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5,
                    }}>
                      This agent will see all live agents in your library as callable tools.
                      It will decide which agent(s) to invoke based on the conversation.
                      Sub-agents run their full pipeline (guardrails, RAG, tools).
                      Max nesting depth: 3 levels.
                    </div>
                  )}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 4 }}>Priority (lower = higher priority)</div>
                    <input type="number" value={agentPriority} onChange={e => setAgentPriority(parseInt(e.target.value) || 100)} min={0} max={999} style={{
                      width: 80, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 10px", color: COLORS.text, fontSize: 12,
                    }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 4 }}>Specialized Intents (leave empty for general handling)</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                      {routingIntents.map((intent, i) => (
                        <span key={i} style={{
                          padding: "3px 8px", borderRadius: 12, fontSize: 10, fontWeight: 500,
                          background: `${COLORS.accent}18`, color: COLORS.accentText, border: `1px solid ${COLORS.accent}30`,
                          display: "flex", alignItems: "center", gap: 4,
                        }}>
                          {intent}
                          <span onClick={() => setRoutingIntents(prev => prev.filter((_, j) => j !== i))} style={{ cursor: "pointer", fontSize: 12, lineHeight: 1 }}>{"\u00D7"}</span>
                        </span>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <input type="text" value={newIntent} onChange={e => setNewIntent(e.target.value)} placeholder="e.g. billing" onKeyDown={e => {
                        if (e.key === "Enter" && newIntent.trim()) { setRoutingIntents(prev => [...prev, newIntent.trim().toLowerCase()]); setNewIntent(""); }
                      }} style={{ flex: 1, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 10px", color: COLORS.text, fontSize: 11 }} />
                      <button onClick={() => { if (newIntent.trim()) { setRoutingIntents(prev => [...prev, newIntent.trim().toLowerCase()]); setNewIntent(""); } }} style={{
                        padding: "6px 10px", borderRadius: 8, border: "none", background: COLORS.accent, color: "#fff", fontSize: 11, cursor: "pointer",
                      }}>Add</button>
                    </div>
                  </div>
                </div>

                {/* Webhook selector — pick from pre-configured webhooks (like MCP servers) */}
                {triggerChannels.webhook && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Linked Webhook</div>
                    {availableWebhooks.length === 0 ? (
                      <div style={{ padding: "10px 12px", borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`, fontSize: 11, color: COLORS.textDim, marginBottom: 6 }}>
                        No webhooks configured. Create one in the Webhooks page first.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {availableWebhooks.filter(wh => wh.status !== "archived").map(wh => {
                          const isSelected = selectedWebhookId === wh.id;
                          return (
                            <div key={wh.id} onClick={() => setSelectedWebhookId(isSelected ? null : wh.id)} style={{
                              padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                              background: isSelected ? `${COLORS.accent}12` : COLORS.bg,
                              border: `1px solid ${isSelected ? COLORS.accent + "40" : COLORS.border}`,
                              transition: "all 0.15s",
                            }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <span style={{ fontSize: 12 }}>{"\uD83D\uDD17"}</span>
                                  <span style={{ fontSize: 12, color: isSelected ? COLORS.accent : COLORS.text, fontWeight: 500 }}>{wh.name}</span>
                                </div>
                                <div style={{
                                  width: 16, height: 16, borderRadius: "50%",
                                  border: `2px solid ${isSelected ? COLORS.accent : COLORS.border}`,
                                  background: isSelected ? COLORS.accent : "transparent",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                  {isSelected && <span style={{ color: "#fff", fontSize: 10, lineHeight: 1 }}>{"\u2713"}</span>}
                                </div>
                              </div>
                              <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 3 }}>
                                {wh.channel} · {wh.action_type} · {wh.auth_mode}
                                {wh.status !== "active" && <span style={{ color: COLORS.orange, marginLeft: 6 }}>({wh.status})</span>}
                              </div>
                              {isSelected && (
                                <div style={{
                                  marginTop: 6, padding: "6px 8px", borderRadius: 6,
                                  background: COLORS.surface, border: `1px solid ${COLORS.green}30`,
                                  fontSize: 10, color: COLORS.green, fontFamily: "monospace", wordBreak: "break-all",
                                }}>/webhooks/inbound/{wh.id}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Smart LLM Routing */}
                <div style={{ width: "100%", height: 1, background: COLORS.border, margin: "16px 0" }} />
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8, fontWeight: 500 }}>Smart Routing</div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: "pointer" }}>
                  <input type="checkbox" checked={nodeConfigs.llm_smart_routing?.enabled || false}
                    onChange={e => {
                      const c = JSON.parse(JSON.stringify(nodeConfigs));
                      if (!c.llm_smart_routing) c.llm_smart_routing = { enabled: false, use_llm_classifier: false, tiers: {} };
                      c.llm_smart_routing.enabled = e.target.checked;
                      setNodeConfigs(c);
                    }} />
                  <span style={{ fontSize: 12, color: COLORS.text }}>Route messages to optimal model by complexity</span>
                </label>

                {nodeConfigs.llm_smart_routing?.enabled && (
                  <>
                    <div style={{
                      padding: "8px 12px", borderRadius: RADIUS.deep, marginBottom: 12,
                      background: `${COLORS.accent}08`, border: `1px solid ${COLORS.accent}18`,
                      fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5,
                    }}>
                      Simple messages (greetings, yes/no) route to fast models. Complex messages (analysis, multi-step) route to powerful models. Standard messages use the default model above.
                    </div>

                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: "pointer" }}>
                      <input type="checkbox" checked={nodeConfigs.llm_smart_routing?.use_llm_classifier || false}
                        onChange={e => {
                          const c = JSON.parse(JSON.stringify(nodeConfigs));
                          c.llm_smart_routing.use_llm_classifier = e.target.checked;
                          setNodeConfigs(c);
                        }} />
                      <span style={{ fontSize: 11, color: COLORS.text }}>Use LLM classifier for uncertain messages (adds ~200ms latency)</span>
                    </label>

                    {[
                      // BYOK-strict: tier defaults pull from the current llmModel state (first available for the provider)
                      { tier: "fast", label: "Fast Tier", desc: "Greetings, simple questions, yes/no", color: COLORS.green, defaultModel: llmModel },
                      { tier: "standard", label: "Standard Tier", desc: "Normal conversations", color: COLORS.accentText, defaultModel: llmModel },
                      { tier: "complex", label: "Complex Tier", desc: "Analysis, code, multi-step reasoning", color: COLORS.orange, defaultModel: llmModel },
                    ].map(t => (
                      <div key={t.tier} style={{
                        padding: 10, borderRadius: 8, marginBottom: 8,
                        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: t.color }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{t.label}</span>
                          <span style={{ fontSize: 10, color: COLORS.textDim }}>— {t.desc}</span>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          {(() => {
                            const tierProv = (nodeConfigs.llm_smart_routing?.tiers?.[t.tier]?.provider) || llmProvider;
                            const tierModels = availableModels[tierProv] || [];
                            const tierModel = (nodeConfigs.llm_smart_routing?.tiers?.[t.tier]?.model) || "";
                            return (<>
                              <select
                                value={tierProv}
                                onChange={e => {
                                  const newProv = e.target.value;
                                  const firstModel = (availableModels[newProv] || [])[0]?.id || "";
                                  const c = JSON.parse(JSON.stringify(nodeConfigs));
                                  if (!c.llm_smart_routing.tiers) c.llm_smart_routing.tiers = {};
                                  if (!c.llm_smart_routing.tiers[t.tier]) c.llm_smart_routing.tiers[t.tier] = {};
                                  c.llm_smart_routing.tiers[t.tier].provider = newProv;
                                  c.llm_smart_routing.tiers[t.tier].model = firstModel;
                                  setNodeConfigs(c);
                                }}
                                style={{ flex: 1, padding: "6px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 11 }}>
                                <option value="openai">OpenAI</option>
                                <option value="anthropic">Anthropic</option>
                                <option value="vertex">Vertex AI</option>
                                <option value="bedrock">Bedrock</option>
                              </select>
                              <select
                                value={tierModel}
                                onChange={e => {
                                  const c = JSON.parse(JSON.stringify(nodeConfigs));
                                  if (!c.llm_smart_routing.tiers) c.llm_smart_routing.tiers = {};
                                  if (!c.llm_smart_routing.tiers[t.tier]) c.llm_smart_routing.tiers[t.tier] = {};
                                  c.llm_smart_routing.tiers[t.tier].model = e.target.value;
                                  setNodeConfigs(c);
                                }}
                                style={{ flex: 2, padding: "6px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 11 }}>
                                {tierModels.length === 0
                                  ? <option value="">No models configured for {tierProv}</option>
                                  : tierModels.map(m => <option key={m.id} value={m.id}>{m.name || m.id}</option>)
                                }
                              </select>
                            </>);
                          })()}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* Fact Checker — inside LLM config so every agent can access it */}
                <div style={{ borderTop: `1px solid ${COLORS.border}`, marginTop: 12, paddingTop: 12 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8, fontWeight: 500 }}>{"\uD83D\uDEE1\uFE0F"} Fact Checker — Anti-Hallucination</div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={nodeConfigs.fact_checker?.enabled} onChange={e => updateNodeConfig("fact_checker", "enabled", e.target.checked)} />
                    <span style={{ fontSize: 12, color: COLORS.text }}>Enable Fact Checker</span>
                  </label>
                  {nodeConfigs.fact_checker?.enabled && (
                    <>
                      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6 }}>Verification Level</div>
                      <select
                        value={nodeConfigs.fact_checker?.level || "standard"}
                        onChange={e => updateNodeConfig("fact_checker", "level", e.target.value)}
                        style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12, marginBottom: 10 }}
                      >
                        <option value="standard">Standard (fast path — rule-based, &lt;5ms)</option>
                        <option value="strict">Strict (fast + slow path — LLM verification, ~200ms)</option>
                        <option value="learn_only">Learn Only (no checks, logs for reward tracking)</option>
                      </select>
                      {nodeConfigs.fact_checker?.level !== "learn_only" && (
                        <>
                          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, cursor: "pointer" }}>
                            <input type="checkbox" checked={nodeConfigs.fact_checker?.verify_numbers !== false} onChange={e => updateNodeConfig("fact_checker", "verify_numbers", e.target.checked)} />
                            <span style={{ fontSize: 12, color: COLORS.text }}>Verify numeric claims against tool results</span>
                          </label>
                          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, cursor: "pointer" }}>
                            <input type="checkbox" checked={nodeConfigs.fact_checker?.require_sources} onChange={e => updateNodeConfig("fact_checker", "require_sources", e.target.checked)} />
                            <span style={{ fontSize: 12, color: COLORS.text }}>Require source citations on factual claims</span>
                          </label>
                          <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4, marginTop: 6 }}>Confidence Threshold</div>
                          <input
                            type="range" min="0.1" max="0.9" step="0.05"
                            value={nodeConfigs.fact_checker?.confidence_threshold || 0.4}
                            onChange={e => updateNodeConfig("fact_checker", "confidence_threshold", parseFloat(e.target.value))}
                            style={{ width: "100%", marginBottom: 4 }}
                          />
                          <div style={{ fontSize: 10, color: COLORS.textMuted, textAlign: "right" }}>{nodeConfigs.fact_checker?.confidence_threshold || 0.4} (lower = more strict)</div>
                        </>
                      )}
                      {nodeConfigs.fact_checker?.level === "learn_only" && (
                        <div style={{ fontSize: 10, color: COLORS.textMuted, lineHeight: 1.4, marginTop: 4 }}>
                          Responses are logged for reward tracking (CSAT, escalation, resolution) without running verification. Use for internal agents like Super Agent or Orchestrator.
                        </div>
                      )}
                    </>
                  )}
                </div>

              </>
            )}

            {/* Input Guardrails config */}
            {selectedNodeData.type === "input_guardrails" && (
              <>
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8, fontWeight: 500 }}>Input Safety Checks</div>
                {[
                  { key: "enabled", label: "Enabled" },
                  { key: "pii_detection", label: "PII Detection" },
                  { key: "injection_detection", label: "Injection Detection" },
                ].map(item => (
                  <label key={item.key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, cursor: "pointer" }}>
                    <input type="checkbox" checked={nodeConfigs.input_guardrails[item.key]} onChange={e => updateNodeConfig("input_guardrails", item.key, e.target.checked)} />
                    <span style={{ fontSize: 12, color: COLORS.text }}>{item.label}</span>
                  </label>
                ))}
              </>
            )}

            {/* Output Guardrails config */}
            {selectedNodeData.type === "output_guardrails" && (
              <>
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8, fontWeight: 500 }}>Output Safety Checks</div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, cursor: "pointer" }}>
                  <input type="checkbox" checked={nodeConfigs.output_guardrails.enabled} onChange={e => updateNodeConfig("output_guardrails", "enabled", e.target.checked)} />
                  <span style={{ fontSize: 12, color: COLORS.text }}>Enabled</span>
                </label>
              </>
            )}

            {/* Condition-specific config */}
            {selectedNodeData.type === "condition" && (
              <>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: "pointer" }}>
                  <input type="checkbox" checked={nodeConfigs.condition.enabled} onChange={e => updateNodeConfig("condition", "enabled", e.target.checked)} />
                  <span style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>Enable Routing</span>
                </label>

                <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8, fontWeight: 500 }}>Intent Routing Rules</div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 10, lineHeight: 1.4 }}>
                  Route conversations based on classified intent. The Pre-Process node must have Intent Classification enabled.
                </div>

                {(nodeConfigs.condition.rules || []).map((rule, idx) => (
                  <div key={idx} style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "center" }}>
                    <select value={rule.intent} onChange={e => {
                      const c = JSON.parse(JSON.stringify(nodeConfigs));
                      c.condition.rules[idx].intent = e.target.value;
                      setNodeConfigs(c);
                    }} style={{ flex: 1, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 11 }}>
                      {["greeting", "question", "complaint", "booking", "billing", "escalation", "farewell", "other"].map(i => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </select>
                    <select value={rule.action} onChange={e => {
                      const c = JSON.parse(JSON.stringify(nodeConfigs));
                      c.condition.rules[idx].action = e.target.value;
                      setNodeConfigs(c);
                    }} style={{ flex: 1, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 11 }}>
                      <option value="continue">Continue</option>
                      <option value="escalate">Escalate</option>
                      <option value="end">End conversation</option>
                    </select>
                    <button onClick={() => {
                      const c = JSON.parse(JSON.stringify(nodeConfigs));
                      c.condition.rules.splice(idx, 1);
                      setNodeConfigs(c);
                    }} style={{ background: "none", border: "none", color: COLORS.pink, cursor: "pointer", fontSize: 14, padding: "2px 6px" }}>x</button>
                  </div>
                ))}
                <button onClick={() => {
                  const c = JSON.parse(JSON.stringify(nodeConfigs));
                  if (!c.condition.rules) c.condition.rules = [];
                  c.condition.rules.push({ intent: "other", action: "continue", label: "" });
                  setNodeConfigs(c);
                }} style={{ background: `${COLORS.orange}15`, border: `1px dashed ${COLORS.orange}40`, borderRadius: 6, padding: "6px 12px", color: COLORS.orange, fontSize: 11, cursor: "pointer", width: "100%", marginBottom: 14 }}>
                  + Add Rule
                </button>

                <div style={{ width: "100%", height: 1, background: COLORS.border, margin: "12px 0" }} />

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Default Action</div>
                  <select value={nodeConfigs.condition.default_action || "continue"} onChange={e => updateNodeConfig("condition", "default_action", e.target.value)}
                    style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.deep, padding: "8px 10px", color: COLORS.text, fontSize: 12 }}>
                    <option value="continue">Continue to LLM</option>
                    <option value="escalate">Escalate to human</option>
                    <option value="end">End conversation</option>
                  </select>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Confidence Threshold: {(nodeConfigs.condition.confidence_threshold || 0.6).toFixed(2)}</div>
                  <input type="range" min={0} max={100} step={5} value={Math.round((nodeConfigs.condition.confidence_threshold || 0.6) * 100)}
                    onChange={e => updateNodeConfig("condition", "confidence_threshold", parseInt(e.target.value) / 100)}
                    style={{ width: "100%", accentColor: COLORS.orange }} />
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>
                    <span>Loose (0.0)</span><span>Strict (1.0)</span>
                  </div>
                </div>
              </>
            )}

            {/* Knowledge-specific config */}
            {selectedNodeData.type === "knowledge" && (
              <>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: "pointer" }}>
                  <input type="checkbox" checked={nodeConfigs.knowledge.enabled} onChange={e => updateNodeConfig("knowledge", "enabled", e.target.checked)} />
                  <span style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>Enable RAG Retrieval</span>
                </label>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Top K Results: {nodeConfigs.knowledge.top_k}</div>
                  <input type="range" min={1} max={20} value={nodeConfigs.knowledge.top_k}
                    onChange={e => updateNodeConfig("knowledge", "top_k", parseInt(e.target.value))}
                    style={{ width: "100%", accentColor: COLORS.cyan }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Score Threshold: {(nodeConfigs.knowledge.score_threshold ?? 0.3).toFixed(2)}</div>
                  <input type="range" min={0} max={100} step={5} value={Math.round((nodeConfigs.knowledge.score_threshold ?? 0.3) * 100)}
                    onChange={e => updateNodeConfig("knowledge", "score_threshold", parseInt(e.target.value) / 100)}
                    style={{ width: "100%", accentColor: COLORS.cyan }} />
                </div>
              </>
            )}

            {/* Action-specific config */}
            {selectedNodeData.type === "action" && (
              <>
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8, fontWeight: 500 }}>Send Response</div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={nodeConfigs.action.send_response?.enabled ?? true} onChange={e => updateNodeConfig("action", "send_response.enabled", e.target.checked)} />
                  <span style={{ fontSize: 12, color: COLORS.text }}>Enable Send Response</span>
                </label>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Response Format</div>
                  <select value={nodeConfigs.action.send_response?.format ?? "text"} onChange={e => updateNodeConfig("action", "send_response.format", e.target.value)}
                    style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.deep, padding: "8px 10px", color: COLORS.text, fontSize: 12 }}>
                    <option value="text">Plain Text</option>
                    <option value="rich">Rich (Cards/Buttons)</option>
                    <option value="markdown">Markdown</option>
                  </select>
                </div>
                <div style={{ width: "100%", height: 1, background: COLORS.border, margin: "16px 0" }} />
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8, fontWeight: 500 }}>Escalation</div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={nodeConfigs.action.escalation?.enabled ?? true} onChange={e => updateNodeConfig("action", "escalation.enabled", e.target.checked)} />
                  <span style={{ fontSize: 12, color: COLORS.text }}>Enable Escalation</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={nodeConfigs.action.escalation?.intent_trigger ?? true} onChange={e => updateNodeConfig("action", "escalation.intent_trigger", e.target.checked)} />
                  <span style={{ fontSize: 12, color: COLORS.text }}>Trigger on escalation intent</span>
                </label>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4, marginTop: 8 }}>History Threshold (messages)</div>
                  <input type="number" min={1} max={50} value={nodeConfigs.action.escalation?.history_threshold ?? 12}
                    onChange={e => updateNodeConfig("action", "escalation.history_threshold", parseInt(e.target.value) || 12)}
                    style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.deep, padding: "8px 10px", color: COLORS.text, fontSize: 12 }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Escalation Message</div>
                  <textarea value={nodeConfigs.action.escalation?.message ?? ""} onChange={e => updateNodeConfig("action", "escalation.message", e.target.value)}
                    rows={2} style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.deep, padding: "8px 10px", color: COLORS.text, fontSize: 12, resize: "vertical", fontFamily: "inherit" }} />
                </div>

                {/* ─── Handoff Queues ─── */}
                <div style={{ width: "100%", height: 1, background: COLORS.border, margin: "16px 0" }} />
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 10, fontWeight: 500 }}>Handoff Queues</div>

                {/* WhatsApp Handoff — guard against missing handoff config */}
                {!nodeConfigs.action.handoff ? (
                  <div style={{ padding: "10px 12px", borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`, fontSize: 11, color: COLORS.textDim, marginBottom: 10 }}>
                    No handoff queues configured. Save the agent first to initialize handoff settings.
                  </div>
                ) : (<>

                {/* WhatsApp Handoff */}
                <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: COLORS.accentText, fontWeight: 600, marginBottom: 8 }}>WhatsApp</div>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Handoff Mode</div>
                    <select value={nodeConfigs.action.handoff.whatsapp.mode} onChange={e => updateNodeConfig("action", "handoff.whatsapp.mode", e.target.value)}
                      style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 12 }}>
                      <option value="external_webhook">External Webhook (Queue System)</option>
                      <option value="transfer">Transfer to Agent</option>
                    </select>
                  </div>
                  {nodeConfigs.action.handoff.whatsapp.mode === "external_webhook" ? (
                    <>
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Webhook URL</div>
                        <input type="text" placeholder="https://queue-system.example.com/handoff" value={nodeConfigs.action.handoff.whatsapp.webhook_url}
                          onChange={e => updateNodeConfig("action", "handoff.whatsapp.webhook_url", e.target.value)}
                          style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 12 }} />
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Auth Header</div>
                        <input type="text" placeholder="Bearer your-api-key" value={nodeConfigs.action.handoff.whatsapp.auth_header}
                          onChange={e => updateNodeConfig("action", "handoff.whatsapp.auth_header", e.target.value)}
                          style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 12 }} />
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Agent Phone Number</div>
                        <input type="text" placeholder="+1234567890" value={nodeConfigs.action.handoff.whatsapp.agent_number}
                          onChange={e => updateNodeConfig("action", "handoff.whatsapp.agent_number", e.target.value)}
                          style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 12 }} />
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Agent Name</div>
                        <input type="text" placeholder="Support Agent" value={nodeConfigs.action.handoff.whatsapp.agent_name}
                          onChange={e => updateNodeConfig("action", "handoff.whatsapp.agent_name", e.target.value)}
                          style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 12 }} />
                      </div>
                    </>
                  )}
                  <div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Confirmation Message</div>
                    <textarea value={nodeConfigs.action.handoff.whatsapp.confirmation_message}
                      onChange={e => updateNodeConfig("action", "handoff.whatsapp.confirmation_message", e.target.value)}
                      rows={2} style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 12, resize: "vertical", fontFamily: "inherit" }} />
                  </div>
                </div>

                {/* Chat Handoff */}
                <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: COLORS.accentText, fontWeight: 600, marginBottom: 8 }}>Chat</div>
                  <div style={{ marginBottom: 4 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Queue Name</div>
                    <input type="text" placeholder="e.g., support-tier-2" value={nodeConfigs.action.handoff.chat.queue_name}
                      onChange={e => updateNodeConfig("action", "handoff.chat.queue_name", e.target.value)}
                      style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 12 }} />
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textDim, fontStyle: "italic", marginTop: 6 }}>Chat queue integration — configure queue system details for future integration</div>
                </div>

                {/* Voice / IVR Handoff */}
                <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: COLORS.accentText, fontWeight: 600, marginBottom: 8 }}>Voice / IVR</div>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Transfer Number</div>
                    <input type="text" placeholder="+1234567890" value={nodeConfigs.action.handoff.voice.transfer_number}
                      onChange={e => updateNodeConfig("action", "handoff.voice.transfer_number", e.target.value)}
                      style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 12 }} />
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>IVR Queue ID</div>
                    <input type="text" placeholder="e.g., Genesys queue ID" value={nodeConfigs.action.handoff.voice.ivr_queue_id}
                      onChange={e => updateNodeConfig("action", "handoff.voice.ivr_queue_id", e.target.value)}
                      style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 12 }} />
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textDim, fontStyle: "italic", marginTop: 6 }}>IVR queue integration — configure IVR details for future integration (e.g., Genesys)</div>
                </div>

                {/* SMS Handoff */}
                <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: COLORS.accentText, fontWeight: 600, marginBottom: 8 }}>SMS</div>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Handoff Mode</div>
                    <select value={nodeConfigs.action.handoff.sms?.mode || "external_webhook"} onChange={e => updateNodeConfig("action", "handoff.sms.mode", e.target.value)}
                      style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 12 }}>
                      <option value="external_webhook">External Webhook (Queue System)</option>
                      <option value="forward">Forward to Number</option>
                    </select>
                  </div>
                  {(nodeConfigs.action.handoff.sms?.mode || "external_webhook") === "external_webhook" ? (
                    <>
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Webhook URL</div>
                        <input type="text" placeholder="https://queue-system.example.com/handoff" value={nodeConfigs.action.handoff.sms?.webhook_url || ""}
                          onChange={e => updateNodeConfig("action", "handoff.sms.webhook_url", e.target.value)}
                          style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 12 }} />
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Auth Header</div>
                        <input type="text" placeholder="Bearer your-api-key" value={nodeConfigs.action.handoff.sms?.auth_header || ""}
                          onChange={e => updateNodeConfig("action", "handoff.sms.auth_header", e.target.value)}
                          style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 12 }} />
                      </div>
                    </>
                  ) : (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Forward Number</div>
                      <input type="text" placeholder="+1234567890" value={nodeConfigs.action.handoff.sms?.forward_number || ""}
                        onChange={e => updateNodeConfig("action", "handoff.sms.forward_number", e.target.value)}
                        style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 12 }} />
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Confirmation Message</div>
                    <textarea value={nodeConfigs.action.handoff.sms?.confirmation_message || ""}
                      onChange={e => updateNodeConfig("action", "handoff.sms.confirmation_message", e.target.value)}
                      rows={2} style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 8px", color: COLORS.text, fontSize: 12, resize: "vertical", fontFamily: "inherit" }} />
                  </div>
                </div>
                </>)}
              </>
            )}

            {/* API-specific config */}
            {selectedNodeData.type === "api" && (
              <>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: "pointer" }}>
                  <input type="checkbox" checked={nodeConfigs.api.enabled} onChange={e => updateNodeConfig("api", "enabled", e.target.checked)} />
                  <span style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>Enable MCP Tools</span>
                </label>

                {nodeConfigs.api.enabled && (
                  <>
                    {/* C19 — legacy MCP-server-registry sidebar removed.
                        Per agent-builder-v1.md §10 row 19 + §11 the
                        canvas-driven Integration node path (C5/C6) is
                        the sole way to bind MCP / OpenAPI integrations
                        to an agent inside the Builder. The
                        Admin → MCP Servers surface stays unchanged for
                        registering new servers. flow_config.api.allowed_servers
                        remains a backward-compat field on the runtime
                        side (for non-canvas-authored agents); the Builder
                        no longer mutates it from this node. */}
                    <div style={{
                      padding: "10px 12px", borderRadius: 8,
                      background: `${COLORS.cyan}10`, border: `1px solid ${COLORS.cyan}55`,
                      fontSize: 11, color: COLORS.text, marginBottom: 14, lineHeight: 1.5,
                    }}>
                      Bind MCP / OpenAPI integrations to this agent by
                      dragging from the Integrations section in the
                      palette → connecting to this node. The legacy
                      checkbox-list is gone — see Admin → MCP Servers
                      to register a new server.
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Max Tool Calls per Turn: {nodeConfigs.api.max_tool_calls}</div>
                      <input type="range" min={1} max={20} value={nodeConfigs.api.max_tool_calls}
                        onChange={e => updateNodeConfig("api", "max_tool_calls", parseInt(e.target.value))}
                        style={{ width: "100%", accentColor: COLORS.pink }} />
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>
                        <span>1</span><span>20</span>
                      </div>
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Tool Timeout: {nodeConfigs.api.timeout || 30}s</div>
                      <input type="range" min={5} max={120} step={5} value={nodeConfigs.api.timeout || 30}
                        onChange={e => updateNodeConfig("api", "timeout", parseInt(e.target.value))}
                        style={{ width: "100%", accentColor: COLORS.pink }} />
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>
                        <span>5s</span><span>120s</span>
                      </div>
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>On Tool Failure</div>
                      <select value={nodeConfigs.api.error_handling || "continue"} onChange={e => updateNodeConfig("api", "error_handling", e.target.value)}
                        style={{ width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.deep, padding: "8px 10px", color: COLORS.text, fontSize: 12 }}>
                        <option value="continue">Continue without tool result</option>
                        <option value="retry">Retry once</option>
                        <option value="fail">Stop and report error</option>
                      </select>
                    </div>
                  </>
                )}
              </>
            )}

            {/* Internal Tools config */}
            {selectedNodeData.type === "internal_tools" && (
              <>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: "pointer" }}>
                  <input type="checkbox" checked={nodeConfigs.internal_tools.enabled} onChange={e => updateNodeConfig("internal_tools", "enabled", e.target.checked)} />
                  <span style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>Enable Internal Tools</span>
                </label>

                {nodeConfigs.internal_tools.enabled && (
                  <>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8, fontWeight: 500 }}>Available Toolsets</div>
                    <div style={{
                      padding: "8px 12px", borderRadius: RADIUS.deep, marginBottom: 12,
                      background: `${COLORS.accent}08`, border: `1px solid ${COLORS.accent}20`,
                      fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5,
                    }}>
                      Internal toolsets let the LLM call platform APIs directly during the tool-calling loop.
                      Each toolset is a group of related tools (e.g., builder tools for creating agents).
                    </div>

                    {[
                      { id: "communication", label: "Communication", desc: "Send emails (SES/SMTP) and SMS (Twilio) directly from the agent. Uses per-tenant configured providers.", icon: "\u2709\uFE0F" },
                      { id: "code_interpreter", label: "Code Interpreter", desc: "Write & run Python in a secure sandbox (no network, this workspace's data only) to compute answers and build downloadable files \u2014 Excel, PowerPoint, PDF, charts, HTML. Requires executable skills enabled for the workspace (Admin \u203A Skills \u203A Executable).", icon: "\u2699\uFE0F" },
                      { id: "analysis", label: "Document Analysis", desc: "Map-reduce analysis over document corpus. Query metadata (SQL), find patterns across documents, get corpus stats.", icon: "\u{1F50D}" },
                      { id: "approvals", label: "Approvals", desc: "Human-in-the-loop. Tools: propose_action (submit for supervisor approval), list_pending_approvals, get_approval_stats.", icon: "\u2705" },
                      { id: "demo", label: "Demo (CRM + JIRA)", desc: "Simulated CRM lookup, order details, JIRA ticket creation, and confirmation emails for demo scenarios.", icon: "\u{1F3AC}" },
                      { id: "analytics", label: "Analytics", desc: "Agent performance metrics and comparison. Tools: list agents, get agent performance (conversations, CSAT, resolution rate), cross-agent comparison.", icon: "\u{1F4CA}" },
                      { id: "builder", label: "Agent Builder", desc: "Create, update, and configure agents. Tools: list models, list agents, get config, create agent, update agent, list MCP servers.", icon: "\u{1F3ED}" },
                      { id: "tester", label: "Platform Tester", desc: "Channel simulation and validation. Tools: test chat/WhatsApp/SMS/email, voice config, identity resolution, OTP flow, handover codes, health check.", icon: "\u{1F9EA}" },
                      { id: "admin", label: "Admin", desc: "Platform administration. Tools: tenant settings, test integrations, list users, audit log, conversations, queue status, run synthetic journeys.", icon: "\u{1F6E0}" },
                    ].map(ts => {
                      const selected = (nodeConfigs.internal_tools.toolsets || []).includes(ts.id);
                      return (
                        <label key={ts.id} style={{
                          display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10,
                          cursor: "pointer", padding: "10px 12px", borderRadius: 10,
                          background: selected ? `${COLORS.purple}12` : COLORS.bg,
                          border: `1px solid ${selected ? COLORS.purple + "40" : COLORS.border}`,
                          transition: "all 0.15s",
                        }}>
                          <input type="checkbox" checked={selected} onChange={e => {
                            const c = JSON.parse(JSON.stringify(nodeConfigs));
                            if (!c.internal_tools.toolsets) c.internal_tools.toolsets = [];
                            if (e.target.checked) {
                              if (!c.internal_tools.toolsets.includes(ts.id)) c.internal_tools.toolsets.push(ts.id);
                            } else {
                              c.internal_tools.toolsets = c.internal_tools.toolsets.filter(t => t !== ts.id);
                            }
                            setNodeConfigs(c);
                          }} style={{ marginTop: 2 }} />
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{ts.icon} {ts.label}</div>
                            <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2, lineHeight: 1.4 }}>{ts.desc}</div>
                          </div>
                        </label>
                      );
                    })}
                  </>
                )}
              </>
            )}

            {/* Memory-specific config (Elasticsearch) */}
            {selectedNodeData.type === "memory" && (
              <>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: "pointer" }}>
                  <input type="checkbox" checked={nodeConfigs.memory.enabled} onChange={e => updateNodeConfig("memory", "enabled", e.target.checked)} />
                  <span style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>Enable Memory Retrieval</span>
                </label>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Search Type</div>
                  <select value={nodeConfigs.memory.search_type} onChange={e => updateNodeConfig("memory", "search_type", e.target.value)}
                    style={{ width: "100%", padding: "6px 10px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }}>
                    <option value="recent">Recent (chronological)</option>
                    <option value="semantic">Semantic (relevance)</option>
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>History Window: {nodeConfigs.memory.history_window} messages</div>
                  <input type="range" min={1} max={50} value={nodeConfigs.memory.history_window}
                    onChange={e => updateNodeConfig("memory", "history_window", parseInt(e.target.value))}
                    style={{ width: "100%", accentColor: COLORS.cyan }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Index Pattern</div>
                  <input type="text" value={nodeConfigs.memory.index_pattern} onChange={e => updateNodeConfig("memory", "index_pattern", e.target.value)}
                    placeholder="conversations-*"
                    style={{ width: "100%", padding: "6px 10px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }} />
                </div>
              </>
            )}

            {/* Grounding (Vertex AI Search) config */}
            {selectedNodeData.type === "grounding" && (
              <>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: "pointer" }}>
                  <input type="checkbox" checked={nodeConfigs.grounding.enabled}
                    onChange={e => updateNodeConfig("grounding", "enabled", e.target.checked)} />
                  <span style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>Enable Vertex AI Grounding</span>
                </label>
                <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 16, lineHeight: 1.5 }}>
                  Retrieves documents from Vertex AI Search data stores to ground LLM responses with citations. Requires GCP configuration in the Dataplex admin panel.
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Data Store IDs</div>
                  <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 8 }}>
                    Specify which Vertex AI Search data stores to use for grounding. Leave empty to use tenant defaults. Each data store adds a retrieval source for the LLM.
                  </div>
                  {(nodeConfigs.grounding.data_store_ids || []).map((dsId, idx) => (
                    <div key={idx} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      <input type="text" value={dsId}
                        onChange={e => {
                          const ids = [...(nodeConfigs.grounding.data_store_ids || [])];
                          ids[idx] = e.target.value;
                          updateNodeConfig("grounding", "data_store_ids", ids);
                        }}
                        placeholder="data-store-id"
                        style={{ flex: 1, padding: "6px 10px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }} />
                      <button onClick={() => {
                        const ids = (nodeConfigs.grounding.data_store_ids || []).filter((_, i) => i !== idx);
                        updateNodeConfig("grounding", "data_store_ids", ids);
                      }} style={{ background: "none", border: "none", color: COLORS.pink, cursor: "pointer", fontSize: 14, padding: "0 4px" }} title="Remove">&times;</button>
                    </div>
                  ))}
                  <button onClick={() => {
                    const ids = [...(nodeConfigs.grounding.data_store_ids || []), ""];
                    updateNodeConfig("grounding", "data_store_ids", ids);
                  }} style={{ background: "none", border: `1px dashed ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, cursor: "pointer", fontSize: 11, padding: "6px 12px", width: "100%" }}>
                    + Add Data Store
                  </button>
                </div>
              </>
            )}

            {/* Pre-Process config */}
            {selectedNodeData.type === "preprocess" && (
              <>
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8, fontWeight: 500 }}>Pipeline Functions</div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={nodeConfigs.preprocess.language_detection?.enabled ?? true}
                    onChange={e => { const c = JSON.parse(JSON.stringify(nodeConfigs)); if (!c.preprocess.language_detection) c.preprocess.language_detection = {}; c.preprocess.language_detection.enabled = e.target.checked; setNodeConfigs(c); }} />
                  <span style={{ fontSize: 12, color: COLORS.text }}>Language Detection</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={nodeConfigs.preprocess.intent_classification?.enabled ?? true}
                    onChange={e => { const c = JSON.parse(JSON.stringify(nodeConfigs)); if (!c.preprocess.intent_classification) c.preprocess.intent_classification = {}; c.preprocess.intent_classification.enabled = e.target.checked; setNodeConfigs(c); }} />
                  <span style={{ fontSize: 12, color: COLORS.text }}>Intent Classification</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={nodeConfigs.preprocess.sentiment_analysis?.enabled ?? true}
                    onChange={e => { const c = JSON.parse(JSON.stringify(nodeConfigs)); if (!c.preprocess.sentiment_analysis) c.preprocess.sentiment_analysis = {}; c.preprocess.sentiment_analysis.enabled = e.target.checked; setNodeConfigs(c); }} />
                  <span style={{ fontSize: 12, color: COLORS.text }}>Sentiment Analysis</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: "pointer" }}>
                  <input type="checkbox" checked={nodeConfigs.preprocess.attachment_processing?.enabled ?? true}
                    onChange={e => { const c = JSON.parse(JSON.stringify(nodeConfigs)); if (!c.preprocess.attachment_processing) c.preprocess.attachment_processing = {}; c.preprocess.attachment_processing.enabled = e.target.checked; setNodeConfigs(c); }} />
                  <span style={{ fontSize: 12, color: COLORS.text }}>Attachment Processing</span>
                </label>
              </>
            )}

            {/* Function-specific config */}
            {selectedNodeData.type === "function" && (
              <>
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8, fontWeight: 500 }}>Custom Code</div>
                <div style={{ padding: "12px 14px", borderRadius: 8, background: `${COLORS.accent}10`, border: `1px solid ${COLORS.border}`, fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>
                  Custom JavaScript/Python functions coming soon. Use the <strong style={{ color: COLORS.text }}>Pre-Process</strong> node for built-in language detection, intent classification, and attachment processing.
                </div>
              </>
            )}

            {/* Agent-specific config */}
            {selectedNodeData.type === "agent" && (() => {
              const cfg = selectedNodeData.agentConfig || {};
              const filteredAgents = availableAgents.filter(a => a.id !== currentAgentId && !a.workflow_id);
              return (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Sub-Agent</div>
                    <select
                      value={cfg.agentId || ""}
                      onChange={e => {
                        const selected = availableAgents.find(a => a.id === e.target.value);
                        updateAgentNodeConfig(selectedNode, {
                          agentId: selected?.id || null,
                          agentName: selected?.name || null,
                          agentStatus: selected?.status || null,
                        });
                      }}
                      style={{
                        width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                        borderRadius: RADIUS.deep, padding: "8px 10px", color: COLORS.text, fontSize: 12, outline: "none", cursor: "pointer",
                      }}
                    >
                      <option value="">Select an agent...</option>
                      {filteredAgents.map(a => (
                        <option key={a.id} value={a.id}>{a.name} ({a.status || "draft"})</option>
                      ))}
                    </select>
                    {cfg.agentId && cfg.agentStatus && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                        <div style={{
                          width: 7, height: 7, borderRadius: "50%",
                          background: cfg.agentStatus === "live" ? COLORS.green : cfg.agentStatus === "paused" ? COLORS.orange : COLORS.textDim,
                        }} />
                        <span style={{ fontSize: 10, color: COLORS.textMuted }}>{cfg.agentName} — {cfg.agentStatus}</span>
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Execution Mode</div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {["sequential", "parallel", "conditional"].map(mode => (
                        <button key={mode} onClick={() => updateAgentNodeConfig(selectedNode, { executionMode: mode })}
                          style={{
                            flex: 1, padding: "6px 0", borderRadius: 6, border: `1px solid ${cfg.executionMode === mode ? COLORS.purple : COLORS.border}`,
                            background: cfg.executionMode === mode ? `${COLORS.purple}15` : COLORS.bg,
                            color: cfg.executionMode === mode ? COLORS.purple : COLORS.textMuted,
                            fontSize: 10, fontWeight: 600, cursor: "pointer", textTransform: "capitalize",
                          }}
                        >{mode}</button>
                      ))}
                    </div>
                  </div>

                  {/* C3: routing sensitivity — supervisor node only. Writes
                      flow_config.supervisor.routing {floor,margin} via the tree
                      save (buildSupervisorPatch). Strict (default) == today. */}
                  {cfg.executionMode === "supervisor" && (() => {
                    const rc = routingConfig || DEFAULT_ROUTING;
                    const s = configToSensitivity(rc);
                    return (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
                          Routing Sensitivity
                          <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.purple }}>{sensitivityLabel(s)}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 10, color: COLORS.textDim }}>Strict</span>
                          <input type="range" min={0} max={1} step={0.01} value={s}
                            onChange={e => setRoutingConfig(sensitivityToConfig(parseFloat(e.target.value)))}
                            style={{ flex: 1 }} />
                          <span style={{ fontSize: 10, color: COLORS.textDim }}>Lenient</span>
                        </div>
                        <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4, fontFamily: "monospace" }}>
                          floor {rc.floor} · margin {rc.margin}
                        </div>
                      </div>
                    );
                  })()}

                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Input Mapping</div>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer" }}>
                      <input type="checkbox" checked={cfg.inputMapping?.passFullContext !== false}
                        onChange={e => updateAgentNodeConfig(selectedNode, { inputMapping: { ...cfg.inputMapping, passFullContext: e.target.checked } })} />
                      <span style={{ fontSize: 12, color: COLORS.text }}>Pass full context</span>
                    </label>
                    <textarea
                      value={cfg.inputMapping?.customPrompt || ""}
                      onChange={e => updateAgentNodeConfig(selectedNode, { inputMapping: { ...cfg.inputMapping, customPrompt: e.target.value } })}
                      placeholder="Custom instructions for this agent step..."
                      rows={2}
                      style={{
                        width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                        borderRadius: RADIUS.deep, padding: "8px 10px", color: COLORS.text, fontSize: 11, resize: "vertical",
                        fontFamily: "inherit", outline: "none",
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>Output Handling</div>
                    <select
                      value={cfg.outputHandling?.mergeStrategy || "replace"}
                      onChange={e => updateAgentNodeConfig(selectedNode, { outputHandling: { ...cfg.outputHandling, mergeStrategy: e.target.value } })}
                      style={{
                        width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                        borderRadius: RADIUS.deep, padding: "8px 10px", color: COLORS.text, fontSize: 12, outline: "none", cursor: "pointer",
                      }}
                    >
                      <option value="replace">Replace (use last output)</option>
                      <option value="concatenate">Concatenate all outputs</option>
                      <option value="llm_summarize">LLM Summarize</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 500 }}>Timeout</div>
                      <span style={{ fontSize: 11, color: COLORS.text, fontWeight: 700 }}>{(cfg.timeout_ms || 30000) / 1000}s</span>
                    </div>
                    <input type="range" min={5000} max={120000} step={5000} value={cfg.timeout_ms || 30000}
                      onChange={e => updateAgentNodeConfig(selectedNode, { timeout_ms: Number(e.target.value) })}
                      style={{ width: "100%", accentColor: COLORS.purple }} />
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                      <span style={{ fontSize: 9, color: COLORS.textDim }}>5s</span>
                      <span style={{ fontSize: 9, color: COLORS.textDim }}>120s</span>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 500 }}>On Error</div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {["fail", "skip", "fallback"].map(s => (
                        <button key={s} onClick={() => updateAgentNodeConfig(selectedNode, { errorHandling: { ...cfg.errorHandling, strategy: s } })}
                          style={{
                            flex: 1, padding: "6px 0", borderRadius: 6,
                            border: `1px solid ${cfg.errorHandling?.strategy === s ? COLORS.pink : COLORS.border}`,
                            background: cfg.errorHandling?.strategy === s ? `${COLORS.pink}15` : COLORS.bg,
                            color: cfg.errorHandling?.strategy === s ? COLORS.pink : COLORS.textMuted,
                            fontSize: 10, fontWeight: 600, cursor: "pointer", textTransform: "capitalize",
                          }}
                        >{s}</button>
                      ))}
                    </div>
                    {cfg.errorHandling?.strategy === "fallback" && (
                      <textarea
                        value={cfg.errorHandling?.fallbackResponse || ""}
                        onChange={e => updateAgentNodeConfig(selectedNode, { errorHandling: { ...cfg.errorHandling, fallbackResponse: e.target.value } })}
                        placeholder="Fallback response if this step fails..."
                        rows={2}
                        style={{
                          width: "100%", boxSizing: "border-box", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                          borderRadius: RADIUS.deep, padding: "8px 10px", color: COLORS.text, fontSize: 11, resize: "vertical",
                          fontFamily: "inherit", outline: "none", marginTop: 8,
                        }}
                      />
                    )}
                  </div>
                </>
              );
            })()}

            {/* Connections for this node */}
            <div style={{ marginTop: 8, fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
              Connections
            </div>
            <div style={{ marginBottom: 16 }}>
              {connections.filter(c => c.from === selectedNode || c.to === selectedNode).length === 0 && (
                <div style={{ fontSize: 11, color: COLORS.textDim, padding: "8px 0" }}>No connections yet. Drag from a port to connect.</div>
              )}
              {connections.map((c, connIdx) => {
                if (c.from !== selectedNode && c.to !== selectedNode) return null;
                const other = c.from === selectedNode ? c.to : c.from;
                const otherNode = nodes.find(n => n.id === other);
                const dir = c.from === selectedNode ? "\u2192" : "\u2190";
                return otherNode ? (
                  <div key={connIdx} style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "6px 8px",
                    borderRadius: 6, marginBottom: 3, background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                  }}>
                    <span style={{ fontSize: 11, color: COLORS.textDim }}>{dir}</span>
                    <span style={{ fontSize: 14 }}>{otherNode.icon}</span>
                    <span style={{ fontSize: 11, color: COLORS.text, fontWeight: 500, flex: 1 }}>{otherNode.label}</span>
                    <button onClick={() => handleDeleteConnection(connIdx)} title="Remove connection" style={{
                      width: 18, height: 18, borderRadius: 4, border: "none",
                      background: "transparent", color: COLORS.textDim, fontSize: 11,
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                      padding: 0, flexShrink: 0,
                    }}
                      onMouseEnter={e => { e.currentTarget.style.color = COLORS.pink; e.currentTarget.style.background = `${COLORS.pink}15`; }}
                      onMouseLeave={e => { e.currentTarget.style.color = COLORS.textDim; e.currentTarget.style.background = "transparent"; }}
                    >{"\u2715"}</button>
                  </div>
                ) : null;
              })}
            </div>

            <div style={{ marginTop: 8, fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>
              Connected Channels
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {[
                { key: "whatsapp", label: "WhatsApp", icon: "\uD83D\uDCAC" },
                { key: "voice", label: "Voice", icon: "\uD83D\uDCDE" },
                { key: "chat", label: "Chat", icon: "\uD83C\uDF10" },
                { key: "sms", label: "SMS", icon: "\uD83D\uDCF1" },
                { key: "email", label: "Email", icon: "\u2709" },
                { key: "webhook", label: "Webhook", icon: "\uD83D\uDD17" },
              ].map(ch => {
                const active = triggerChannels[ch.key];
                return (
                  <div key={ch.key} style={{
                    flex: 1, padding: "10px 0", borderRadius: 10, textAlign: "center",
                    background: active ? `${COLORS.green}12` : COLORS.bg,
                    border: `1px solid ${active ? COLORS.green + "40" : COLORS.border}`,
                    transition: "all 0.15s",
                  }}>
                    <div style={{ fontSize: 16 }}>{ch.icon}</div>
                    <div style={{ fontSize: 10, color: active ? COLORS.green : COLORS.textMuted, marginTop: 4, fontWeight: 500 }}>{ch.label}</div>
                  </div>
                );
              })}
            </div>
        </>
      </div>
      )}

      <Modal open={showLoadModal} onClose={() => setShowLoadModal(false)} title="Load Agent" width={380} zIndex={300}>
            <div style={{ maxHeight: "calc(70vh - 120px)", overflowY: "auto" }}>
              {loadingAgents ? (
                <div style={{ padding: 20, textAlign: "center", color: COLORS.textMuted, fontSize: 12 }}>Loading...</div>
              ) : savedAgents.length === 0 ? (
                <div style={{ padding: 20, textAlign: "center", color: COLORS.textMuted, fontSize: 12 }}>No saved agents found.</div>
              ) : (
                savedAgents.map(agent => (
                  <div key={agent.id} onClick={() => handleLoadAgent(agent)} style={{
                    padding: "12px 14px", borderRadius: 10, marginBottom: 6, cursor: "pointer",
                    background: agent.id === currentAgentId ? `${COLORS.accent}12` : COLORS.bg,
                    border: `1px solid ${agent.id === currentAgentId ? COLORS.accent + "40" : COLORS.border}`,
                    transition: "all 0.15s",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{agent.name}</span>
                      <Badge color={COLORS.cyan}>{fmtVer(agent.version) || "v1.0"}</Badge>
                    </div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
                      {agent.model || "No model"}{agent.provider ? ` \u00B7 ${agent.provider}` : ""}
                    </div>
                    <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2, display: "flex", gap: 8 }}>
                      <span>{agent.status || "draft"}</span>
                      {agent.metadata_json?.canvas && <span>{"\u00B7"} {agent.metadata_json.canvas.nodes?.length || 0} nodes</span>}
                      {agent.channels?.length > 0 && <span>{"\u00B7"} {agent.channels.join(", ")}</span>}
                    </div>
                  </div>
                ))
              )}
            </div>
      </Modal>

      {/* Save As modal */}
      <Modal open={showSaveAsModal} onClose={() => setShowSaveAsModal(false)} title="Save As New Agent" width={360} zIndex={300}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 6 }}>Agent Name</label>
                <input
                  type="text" value={saveAsName} onChange={e => setSaveAsName(e.target.value)}
                  placeholder="Enter agent name"
                  autoFocus
                  style={{
                    width: "100%", padding: "10px 12px", borderRadius: 8,
                    border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                    color: COLORS.text, fontSize: 13, outline: "none",
                  }}
                  onFocus={e => e.target.style.borderColor = COLORS.accent}
                  onBlur={e => e.target.style.borderColor = COLORS.border}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 6 }}>Version</label>
                <input
                  type="text" value={saveAsVersion} onChange={e => setSaveAsVersion(e.target.value)}
                  placeholder="v1.0"
                  style={{
                    width: "100%", padding: "10px 12px", borderRadius: 8,
                    border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                    color: COLORS.text, fontSize: 13, outline: "none",
                  }}
                  onFocus={e => e.target.style.borderColor = COLORS.accent}
                  onBlur={e => e.target.style.borderColor = COLORS.border}
                />
              </div>
              <button
                onClick={handleSaveAsAgent}
                disabled={saving || !saveAsName.trim()}
                style={{
                  width: "100%", padding: "10px 0", borderRadius: 8, border: "none",
                  background: !saveAsName.trim() ? COLORS.textDim : `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
                  color: "#fff", fontSize: 13, fontWeight: 600,
                  cursor: !saveAsName.trim() ? "not-allowed" : "pointer",
                  opacity: saving ? 0.6 : 1,
                }}
              >{saving ? "Saving..." : "Save as New Agent"}</button>
      </Modal>

      {/* Save New Agent modal (shown when Save is clicked on a new flow) */}
      <Modal open={showSaveNewModal} onClose={() => setShowSaveNewModal(false)} title="Save Agent" width={360} zIndex={300}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 6 }}>Agent Name</label>
                <input
                  type="text" value={saveNewName} onChange={e => setSaveNewName(e.target.value)}
                  placeholder="Enter agent name"
                  autoFocus
                  style={{
                    width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8,
                    border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                    color: COLORS.text, fontSize: 13, outline: "none",
                  }}
                  onFocus={e => e.target.style.borderColor = COLORS.accent}
                  onBlur={e => e.target.style.borderColor = COLORS.border}
                  onKeyDown={e => { if (e.key === "Enter" && saveNewName.trim()) handleSaveNewAgent(); }}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 6 }}>Version</label>
                <input
                  type="text" value={saveNewVersion} onChange={e => setSaveNewVersion(e.target.value)}
                  placeholder="v1.0"
                  style={{
                    width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8,
                    border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                    color: COLORS.text, fontSize: 13, outline: "none",
                  }}
                  onFocus={e => e.target.style.borderColor = COLORS.accent}
                  onBlur={e => e.target.style.borderColor = COLORS.border}
                  onKeyDown={e => { if (e.key === "Enter" && saveNewName.trim()) handleSaveNewAgent(); }}
                />
              </div>
              <button
                onClick={handleSaveNewAgent}
                disabled={saving || !saveNewName.trim()}
                style={{
                  width: "100%", padding: "10px 0", borderRadius: 8, border: "none",
                  background: !saveNewName.trim() ? COLORS.textDim : `linear-gradient(135deg, ${COLORS.green}, ${COLORS.cyan})`,
                  color: "#fff", fontSize: 13, fontWeight: 600,
                  cursor: !saveNewName.trim() ? "not-allowed" : "pointer",
                  opacity: saving ? 0.6 : 1,
                }}
              >{saving ? "Saving..." : "Save Agent"}</button>
      </Modal>

      {/* C23 — Import tree modal */}
      <Modal open={showImportModal} onClose={() => setShowImportModal(false)} title="Import Tree from JSON" width={520} zIndex={300}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>
            Paste a tree-snapshot JSON exported from any tenant. The server runs §5.4 validation: schema check, prompt-injection sanitizer, credential strip, integration-ref validation. Admin role required.
          </div>
          <textarea
            value={importJsonText}
            onChange={e => setImportJsonText(e.target.value)}
            placeholder='{"schema_version":"1","kind":"supervisor","supervisor":{...},"experts":[...],"integration_refs":{...}}'
            style={{
              width: "100%", minHeight: 220, padding: "10px 12px",
              borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`,
              background: COLORS.surface, color: COLORS.text,
              fontFamily: "monospace", fontSize: 11, outline: "none",
              boxSizing: "border-box", resize: "vertical",
            }}
          />
          {importErr && (
            <div style={{
              padding: "8px 10px", borderRadius: 6,
              background: `${COLORS.pink}18`, color: COLORS.text, fontSize: 11,
              border: `1px solid ${COLORS.pink}55`,
            }}>{importErr}</div>
          )}
          <button onClick={handleImportTree} disabled={!importJsonText.trim() || importBusy} style={{
            width: "100%", padding: "10px 0", borderRadius: 8, border: "none",
            background: !importJsonText.trim() || importBusy ? COLORS.textDim : COLORS.accent,
            color: "#fff", fontSize: 13, fontWeight: 600,
            cursor: !importJsonText.trim() || importBusy ? "not-allowed" : "pointer",
          }}>{importBusy ? "Importing…" : "Import"}</button>
        </div>
      </Modal>

      {/* C23 — Publish to Marketplace modal */}
      <Modal open={showPublishModal} onClose={() => setShowPublishModal(false)} title="Publish to Marketplace" width={460} zIndex={300}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>
            Snapshots the current canvas's tree into the marketplace `templates` table. Strips credentials. visibility='global' requires admin role.
          </div>
          <input value={publishName} onChange={e => setPublishName(e.target.value)} placeholder="Template name *" style={{
            padding: "8px 10px", borderRadius: 6,
            border: `1px solid ${COLORS.border}`, background: COLORS.surface,
            color: COLORS.text, fontSize: 12, outline: "none",
          }} />
          <input value={publishDescription} onChange={e => setPublishDescription(e.target.value)} placeholder="Description (optional)" style={{
            padding: "8px 10px", borderRadius: 6,
            border: `1px solid ${COLORS.border}`, background: COLORS.surface,
            color: COLORS.text, fontSize: 12, outline: "none",
          }} />
          <select value={publishCategory} onChange={e => setPublishCategory(e.target.value)} style={{
            padding: "8px 10px", borderRadius: 6,
            border: `1px solid ${COLORS.border}`, background: COLORS.surface,
            color: COLORS.text, fontSize: 12, outline: "none", cursor: "pointer",
          }}>
            <option value="general">General</option>
            <option value="customer-support">Customer Support</option>
            <option value="sales">Sales</option>
            <option value="coding">Coding</option>
            <option value="writing">Writing</option>
            <option value="research">Research</option>
          </select>
          <select value={publishVisibility} onChange={e => setPublishVisibility(e.target.value)} style={{
            padding: "8px 10px", borderRadius: 6,
            border: `1px solid ${COLORS.border}`, background: COLORS.surface,
            color: COLORS.text, fontSize: 12, outline: "none", cursor: "pointer",
          }}>
            <option value="tenant">Tenant — visible to your workspace only</option>
            <option value="public">Public — visible to your tenant marketplace</option>
            <option value="private">Private — author-only</option>
            <option value="global">Global — cross-tenant (admin only)</option>
          </select>
          {publishErr && (
            <div style={{
              padding: "8px 10px", borderRadius: 6,
              background: `${COLORS.pink}18`, color: COLORS.text, fontSize: 11,
              border: `1px solid ${COLORS.pink}55`,
            }}>{publishErr}</div>
          )}
          <button onClick={handlePublishTree} disabled={!publishName.trim() || publishBusy} style={{
            width: "100%", padding: "10px 0", borderRadius: 8, border: "none",
            background: !publishName.trim() || publishBusy ? COLORS.textDim : COLORS.purple,
            color: "#fff", fontSize: 13, fontWeight: 600,
            cursor: !publishName.trim() || publishBusy ? "not-allowed" : "pointer",
          }}>{publishBusy ? "Publishing…" : "Publish"}</button>
        </div>
      </Modal>
    </div>
  );
};

export default BuilderView;
